import React, { useState, useEffect, useCallback, useRef } from 'react'
import StatusPill from '../components/StatusPill'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import ComponentSpecsAdmin from '../components/ComponentSpecsAdmin'
import ReviewSettings from '../components/ReviewSettings'
import LegacyImport from './LegacyImport'
import WorkshopVatSettings from '../components/WorkshopVatSettings'
import { DEFAULT_LOYALTY_CATEGORIES, LOYALTY_CATEGORY_OPTIONS, loyaltyRuleSummary, normaliseLoyaltyCategories } from '../lib/loyalty'
import {
  buildGeneralServiceImportPreviewRows,
  generalServiceImportDuplicateKey,
  generalServiceImportRowSkipReason,
  generalServices,
  isGeneralServiceImportRowIncluded,
  isPilotGeneralChecklistServiceName,
  summariseGeneralChecklistAssignmentResults,
  suspensionServices,
} from '../lib/serviceMenu'
import {
  STOCK_UNIT_TYPES,
  isLowStock,
  isMissingStockSchemaError,
  stockItemMeta,
  stockItemSearchText,
  stockItemTitle,
  stockMoneyLabel,
  stockQuantity,
  stockQuantityLabel,
} from '../lib/stock'
import { runCapacitySettingsUpdate } from '../lib/jobLifecycle'
import {
  activeServiceCardAssignment,
  groupServiceCards,
  parseServiceCardSelection,
  serviceCardPreviewItems,
  serviceCardSelectionValue,
  serviceSetupIssues,
} from '../lib/serviceCards'

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function initialSettingsTab() {
  if (typeof window === 'undefined') return 'services'
  return new URLSearchParams(window.location.search).get('tab') || 'services'
}

function launchChecklistKey(workshopId) {
  return `rebound_launch_checklist_hidden_${workshopId}`
}

function fmtDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

const WORKSHOP_SETTINGS_COLUMNS = `
  id, name, email, phone, address, logo_url, slug, max_jobs_per_day,
  booking_notification_email, invoice_prefix, bank_account_name,
  bank_sort_code, bank_account_number, reminders_enabled,
  weekly_bookable_hours,
  discount_approval_required, service_passport_enabled, loyalty_enabled,
  loyalty_required_completed_services, loyalty_reward_type,
  loyalty_reward_value, loyalty_eligible_categories,
  stripe_connected_account_id, stripe_connect_charges_enabled,
  stripe_connect_payouts_enabled, stripe_connect_onboarding_complete,
  stripe_connect_disabled_reason, stripe_connect_last_checked_at,
  stripe_connect_onboarding_started_at,
  subscription_status, subscription_end, general_workshop_enabled
`

const STANDARD_CONNECT_MESSAGE = 'Connect an existing Stripe Standard account to take customer card payments. Bank transfer details and manual paid tracking remain available.'
const LOGO_BUCKET = 'workshop-logos'
const LOGO_MAX_FILE_SIZE = 2 * 1024 * 1024
const LOGO_FILE_TYPES = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
}

const GENERAL_PRICING_MODES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'from', label: 'From' },
  { value: 'quote_required', label: 'Quote required' },
]

const GENERAL_VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public/bookable' },
  { value: 'internal', label: 'Internal/workshop-only' },
  { value: 'archived', label: 'Archived/hidden' },
]

const GENERAL_BOOKING_TYPES = [
  { value: 'direct_bookable', label: 'Direct-bookable' },
  { value: 'assessment_first', label: 'Assessment-first' },
  { value: 'quote_only', label: 'Quote-only' },
  { value: 'internal_addon', label: 'Internal add-on' },
]

const GENERAL_PREVIEW_NEW_CATEGORY = '__new__'

function capacityHoursInput(value) {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

function capacityTeamRows(rows = []) {
  return rows.map(member => ({
    id: member.id,
    full_name: member.full_name || '',
    role: member.role || '',
    weekly_bookable_hours: capacityHoursInput(member.weekly_bookable_hours),
    calendar_colour: member.calendar_colour || '',
  }))
}

function optionLabel(options, value, fallback = 'Unknown') {
  return options.find(option => option.value === value)?.label || fallback
}

function sortByOrderThenName(a, b) {
  const orderA = Number(a?.sort_order || 0)
  const orderB = Number(b?.sort_order || 0)
  if (orderA !== orderB) return orderA - orderB
  return String(a?.name || '').localeCompare(String(b?.name || ''))
}

function humaniseCategory(value) {
  const text = String(value || '').replace(/[-_]/g, ' ').trim()
  if (!text || text === 'general') return 'Uncategorised'
  return text.replace(/\b\w/g, letter => letter.toUpperCase())
}

function formatGeneralPrice(service) {
  if (service.pricing_mode === 'quote_required') return 'Quote required'
  const price = Number(service.base_price || 0).toFixed(2)
  return `${service.pricing_mode === 'from' ? 'From ' : ''}£${price}`
}

function importPriceLabel(service) {
  if (service.pricing_mode === 'quote_required') return 'Quote required'
  const price = Number(service.base_price || 0).toFixed(2)
  return `${service.pricing_mode === 'from' ? 'From ' : ''}£${price}`
}

function uniqueReasons(reasons = []) {
  return [...new Set((reasons || []).filter(Boolean))]
}

function withoutCategoryReasons(reasons = []) {
  return (reasons || []).filter(reason =>
    !['Category match needs review', 'New category will be created'].includes(reason)
  )
}

export default function Settings() {
  const { workshopId, workshop, profile, refreshWorkshop } = useAuth()
  const isOwner = profile?.role === 'owner' || profile?.is_superadmin
  const localTestMode = process.env.REACT_APP_LOCAL_TEST_MODE === 'true'
  const [services, setServices] = useState([])
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const loadedRef = useRef(false)
  const [tab, setTab] = useState(initialSettingsTab)
  const [pasteText, setPasteText] = useState('')
  const [parsing, setParsing] = useState(false)
  const [preview, setPreview] = useState(null)
  const [parseError, setParseError] = useState(null)
  const [generalPasteText, setGeneralPasteText] = useState('')
  const [generalParsing, setGeneralParsing] = useState(false)
  const [generalPreview, setGeneralPreview] = useState(null)
  const [generalParseError, setGeneralParseError] = useState(null)
  const [generalImportSummary, setGeneralImportSummary] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState(null)
  const [editingService, setEditingService] = useState(null)
  const [previewingServiceCard, setPreviewingServiceCard] = useState(null)
  const [serviceCardBlueprints, setServiceCardBlueprints] = useState([])
  const [serviceCardAssignments, setServiceCardAssignments] = useState([])
  const [editingCategory, setEditingCategory] = useState(null)
  const [serviceMenuModule, setServiceMenuModule] = useState('suspension')
  const [serviceCategories, setServiceCategories] = useState([])
  const [workshopForm, setWorkshopForm] = useState({ name: '', email: '', phone: '', address: '', logo_url: '', slug: '', max_jobs_per_day: 4, weekly_bookable_hours: 30, booking_notification_email: '', invoice_prefix: '', bank_account_name: '', bank_sort_code: '', bank_account_number: '', reminders_enabled: true, discount_approval_required: false, service_passport_enabled: false, loyalty_enabled: false, loyalty_required_completed_services: 4, loyalty_reward_type: 'percent', loyalty_reward_value: 20, loyalty_eligible_categories: DEFAULT_LOYALTY_CATEGORIES, stripe_connected_account_id: '', stripe_connect_account_type: '', stripe_connect_charges_enabled: false, stripe_connect_payouts_enabled: false, stripe_connect_onboarding_complete: false, stripe_connect_disabled_reason: '', stripe_connect_last_checked_at: '', stripe_connect_onboarding_started_at: '', general_workshop_enabled: false })
  const [connectBusy, setConnectBusy] = useState('')
  const [connectMsg, setConnectMsg] = useState(null)
  const [connectError, setConnectError] = useState(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteMsg, setInviteMsg] = useState(null)
  const [inviteError, setInviteError] = useState(null)
  const [capacityTeamForm, setCapacityTeamForm] = useState([])
  const [capacitySaving, setCapacitySaving] = useState(false)
  const [capacityMsg, setCapacityMsg] = useState(null)
  const [capacityError, setCapacityError] = useState('')
  const [launchStats, setLaunchStats] = useState({ jobs: 0, reports: 0, kits: 0 })
  const [launchChecklistHidden, setLaunchChecklistHidden] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoUploadError, setLogoUploadError] = useState('')

  const load = useCallback(async () => {
    if (!workshopId) return
    if (!loadedRef.current) setLoading(true)
    try {
      const [svcRes, categoryRes, teamRes, wsRes, jobCountRes, reportCountRes, kitCountRes, cardRes, assignmentRes] = await Promise.all([
        supabase.from('services').select('*').eq('workshop_id', workshopId).order('category').order('sort_order'),
        supabase.from('service_categories').select('*').eq('workshop_id', workshopId).eq('module_key', 'general').order('active', { ascending: false }).order('sort_order').order('name'),
        supabase.from('profiles').select('id, full_name, role, weekly_bookable_hours, calendar_colour, created_at').eq('workshop_id', workshopId).order('created_at'),
        supabase.from('workshops').select(WORKSHOP_SETTINGS_COLUMNS).eq('id', workshopId).single(),
        supabase.from('jobs').select('id', { count: 'exact', head: true }).eq('workshop_id', workshopId),
        supabase.from('job_report_versions').select('id', { count: 'exact', head: true }).eq('workshop_id', workshopId),
        supabase.from('kit_prices').select('kit_id', { count: 'exact', head: true }).eq('workshop_id', workshopId),
        supabase.from('service_card_blueprints').select('*, items:service_card_blueprint_items(*)').eq('active', true).order('family').order('name').order('version', { ascending: false }),
        supabase.from('service_checklist_assignments').select('service_id, checklist_template_id, active, template:checklist_templates(id, name, source_blueprint_key, source_blueprint_version)').eq('workshop_id', workshopId).eq('active', true),
      ])
      const failed = [svcRes, categoryRes, teamRes, wsRes, jobCountRes, reportCountRes, kitCountRes, cardRes, assignmentRes].find(result => result?.error)
      if (failed?.error) throw failed.error
      setServices(svcRes.data || [])
      setServiceCategories(categoryRes.data || [])
      setServiceCardBlueprints(cardRes.data || [])
      setServiceCardAssignments(assignmentRes.data || [])
      const teamRows = teamRes.data || []
      setTeam(teamRows)
      setCapacityTeamForm(capacityTeamRows(teamRows))
      setLaunchStats({
        jobs: jobCountRes.count || 0,
        reports: reportCountRes.count || 0,
        kits: kitCountRes.count || 0,
      })
      if (wsRes.data) setWorkshopForm({
      name: wsRes.data.name || '',
      email: wsRes.data.email || '',
      phone: wsRes.data.phone || '',
      address: wsRes.data.address || '',
      logo_url: wsRes.data.logo_url || '',
      slug: wsRes.data.slug || '',
      max_jobs_per_day: wsRes.data.max_jobs_per_day || 4,
      weekly_bookable_hours: wsRes.data.weekly_bookable_hours ?? 30,
      booking_notification_email: wsRes.data.booking_notification_email || '',
      invoice_prefix: wsRes.data.invoice_prefix || '',
      bank_account_name: wsRes.data.bank_account_name || '',
      bank_sort_code: wsRes.data.bank_sort_code || '',
      bank_account_number: wsRes.data.bank_account_number || '',
      reminders_enabled: wsRes.data.reminders_enabled !== false,
      discount_approval_required: wsRes.data.discount_approval_required === true,
      service_passport_enabled: wsRes.data.service_passport_enabled === true,
      loyalty_enabled: wsRes.data.loyalty_enabled === true,
      loyalty_required_completed_services: wsRes.data.loyalty_required_completed_services || 4,
      loyalty_reward_type: wsRes.data.loyalty_reward_type || 'percent',
      loyalty_reward_value: wsRes.data.loyalty_reward_value ?? 20,
      loyalty_eligible_categories: normaliseLoyaltyCategories(wsRes.data.loyalty_eligible_categories),
      stripe_connected_account_id: wsRes.data.stripe_connected_account_id || '',
      stripe_connect_account_type: '',
      stripe_connect_charges_enabled: !!wsRes.data.stripe_connect_charges_enabled,
      stripe_connect_payouts_enabled: !!wsRes.data.stripe_connect_payouts_enabled,
      stripe_connect_onboarding_complete: !!wsRes.data.stripe_connect_onboarding_complete,
      stripe_connect_disabled_reason: wsRes.data.stripe_connect_disabled_reason || '',
      stripe_connect_last_checked_at: wsRes.data.stripe_connect_last_checked_at || '',
      stripe_connect_onboarding_started_at: wsRes.data.stripe_connect_onboarding_started_at || '',
      general_workshop_enabled: wsRes.data.general_workshop_enabled === true,
      })
      loadedRef.current = true
      setLoadError('')
    } catch (err) {
      setLoadError(err.message || 'Could not load workshop setup.')
    } finally {
      setLoading(false)
    }
  }, [workshopId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!workshopId || typeof window === 'undefined') return
    setLaunchChecklistHidden(window.localStorage.getItem(launchChecklistKey(workshopId)) === 'true')
  }, [workshopId])

  useEffect(() => {
    if (workshopForm.general_workshop_enabled !== true && serviceMenuModule !== 'suspension') {
      setServiceMenuModule('suspension')
    }
  }, [workshopForm.general_workshop_enabled, serviceMenuModule])

  useEffect(() => {
    if (tab !== 'payments' || !workshopId || typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const connectReturn = url.searchParams.get('stripe_connect')
    if (!connectReturn) return

    if (connectReturn === 'return' || connectReturn === 'refresh') {
      refreshStripeConnectStatus({ fromRedirect: true })
    } else {
      setConnectError(url.searchParams.get('reason') || 'Stripe connection was not completed.')
    }
    url.searchParams.delete('stripe_connect')
    url.searchParams.delete('reason')
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
  }, [tab, workshopId])

  async function parseServices() {
    if (!pasteText.trim()) return
    if (localTestMode) {
      setParseError('AI service parsing is disabled in the isolated local environment.')
      return
    }
    setParsing(true); setParseError(null); setPreview(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before parsing services.')

      const res = await fetch('/.netlify/functions/ai-parse-services', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: pasteText })
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setPreview(data.services)
    } catch (err) {
      setParseError('Could not parse — try tidying up the text and try again. Error: ' + err.message)
    }
    setParsing(false)
  }

  async function importServices() {
    if (!preview) return
    setSaving(true)
    const rows = preview.map((s, i) => ({ ...s, module_key: 'suspension', workshop_id: workshopId, sort_order: i }))
    const { error } = await supabase.from('services').insert(rows)
    if (error) { setParseError(error.message); setSaving(false); return }
    setPreview(null); setPasteText('')
    setSaveMsg('Services imported successfully.')
    await load(); setSaving(false)
    setTimeout(() => setSaveMsg(null), 3000)
  }

  async function ensurePilotChecklistAssignments(servicesToAssign = []) {
    const uniqueServices = Array.from((servicesToAssign || []).reduce((map, service) => {
      if (service?.id && isPilotGeneralChecklistServiceName(service.name)) map.set(service.id, service)
      return map
    }, new Map()).values())

    const results = []
    for (const service of uniqueServices) {
      try {
        const { data, error } = await supabase.rpc('ensure_whole_bike_pilot_checklist_assignment', { p_service_id: service.id })
        if (error) throw error
        const row = Array.isArray(data) ? data[0] : data
        results.push({
          service_name: service.name,
          status: row?.status || 'already_present',
          assignment_id: row?.assignment_id || null,
        })
      } catch (err) {
        results.push({
          service_name: service.name,
          status: 'failed',
          error: err.message || 'Checklist assignment needs manual review.',
        })
      }
    }

    return summariseGeneralChecklistAssignmentResults(results)
  }

  function findActiveGeneralCategoryByInput(value) {
    const key = slugify(value || '')
    const text = String(value || '').trim().toLowerCase()
    return activeGeneralCategories.find(category =>
      category.id === value
      || category.key === key
      || String(category.name || '').trim().toLowerCase() === text
    ) || null
  }

  function updateGeneralPreviewRow(index, updater) {
    setGeneralPreview(rows => (rows || []).map((row, rowIndex) => {
      if (rowIndex !== index) return row
      const next = typeof updater === 'function' ? updater(row) : { ...row, ...updater }
      return {
        ...next,
        include: next.skip ? false : next.include !== false,
      }
    }))
  }

  function updateGeneralPreviewCategory(index, value) {
    updateGeneralPreviewRow(index, row => {
      if (value === GENERAL_PREVIEW_NEW_CATEGORY) {
        const categoryName = row.custom_category_name || row.raw_category_name || row.category_name || 'General Workshop'
        const reviewReasons = uniqueReasons([...withoutCategoryReasons(row.review_reasons), 'New category will be created'])
        return {
          ...row,
          matched_category_id: null,
          category_selection: GENERAL_PREVIEW_NEW_CATEGORY,
          category_name: categoryName,
          category_key: slugify(categoryName) || 'general-workshop',
          custom_category_name: categoryName,
          category_match_type: 'manual_new',
          category_needs_review: true,
          needs_review: reviewReasons.length > 0,
          review_reasons: reviewReasons,
        }
      }

      const category = activeGeneralCategories.find(item => item.id === value)
      if (!category) return row
      const reviewReasons = withoutCategoryReasons(row.review_reasons)
      return {
        ...row,
        matched_category_id: category.id,
        category_selection: category.id,
        category_name: category.name,
        category_key: category.key,
        custom_category_name: '',
        category_match_type: 'manual',
        category_needs_review: false,
        needs_review: reviewReasons.length > 0,
        review_reasons: reviewReasons,
      }
    })
  }

  function updateGeneralPreviewCustomCategory(index, value) {
    updateGeneralPreviewRow(index, row => {
      const existing = findActiveGeneralCategoryByInput(value)
      if (existing) {
        const reviewReasons = withoutCategoryReasons(row.review_reasons)
        return {
          ...row,
          matched_category_id: existing.id,
          category_selection: existing.id,
          category_name: existing.name,
          category_key: existing.key,
          custom_category_name: '',
          category_match_type: 'manual',
          category_needs_review: false,
          needs_review: reviewReasons.length > 0,
          review_reasons: reviewReasons,
        }
      }

      const categoryName = value
      const categoryKey = slugify(categoryName) || 'general-workshop'
      const reviewReasons = uniqueReasons([...withoutCategoryReasons(row.review_reasons), 'New category will be created'])
      return {
        ...row,
        matched_category_id: null,
        category_selection: GENERAL_PREVIEW_NEW_CATEGORY,
        category_name: categoryName,
        category_key: categoryKey,
        custom_category_name: categoryName,
        category_match_type: 'manual_new',
        category_needs_review: true,
        needs_review: reviewReasons.length > 0,
        review_reasons: reviewReasons,
      }
    })
  }

  function updateGeneralPreviewName(index, value) {
    updateGeneralPreviewRow(index, row => {
      const reviewReasons = withoutCategoryReasons(row.review_reasons)
        .filter(reason => reason !== 'Missing service name')
      const hasName = Boolean(String(value || '').trim())
      const wasMissingName = row.skip_reason === 'Missing service name.'
      if (!String(value || '').trim()) reviewReasons.push('Missing service name')
      return {
        ...row,
        name: value,
        skip: wasMissingName && hasName ? false : row.skip,
        skip_reason: wasMissingName && hasName ? '' : row.skip_reason,
        include: wasMissingName && hasName ? true : row.include,
        needs_review: reviewReasons.length > 0 || row.category_needs_review,
        review_reasons: uniqueReasons(reviewReasons),
      }
    })
  }

  async function parseGeneralServices() {
    if (!generalPasteText.trim() || !canManageGeneralServices) return
    if (localTestMode) {
      setGeneralParseError('AI service parsing is disabled in the isolated local environment.')
      return
    }
    setGeneralParsing(true); setGeneralParseError(null); setGeneralPreview(null); setGeneralImportSummary(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before parsing services.')

      const res = await fetch('/.netlify/functions/ai-parse-services', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: generalPasteText, module_key: 'general' })
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      const rows = buildGeneralServiceImportPreviewRows(data.services || [], activeGeneralCategories, {
        sourceText: generalPasteText,
      })
      setGeneralPreview(rows)
    } catch (err) {
      setGeneralParseError('Could not parse General services — try tidying up the text and try again. Error: ' + err.message)
    }
    setGeneralParsing(false)
  }

  async function importGeneralServices() {
    if (!generalPreview || !canManageGeneralServices) return
    setSaving(true)
    setGeneralParseError(null)
    setGeneralImportSummary(null)

    const skipped = []
    const reviewRows = []
    const categoryByKey = new Map(serviceCategories.map(category => [category.key, category]))
    const existingServiceByKey = new Map()
    generalServiceRows.forEach(service => {
      const category = serviceCategories.find(item => item.id === service.service_category_id)
      const key = generalServiceImportDuplicateKey(service, category?.key || service.category || 'general-workshop')
      if (!existingServiceByKey.has(key)) existingServiceByKey.set(key, service)
    })
    const existingKeys = new Set(existingServiceByKey.keys())
    const previewKeys = new Set()
    const createdCategories = []
    const reusedCategoryKeys = new Set()
    const assignmentCandidates = new Map()
    let nextCategoryOrder = serviceCategories.length
      ? Math.max(...serviceCategories.map(category => Number(category.sort_order || 0))) + 10
      : 10
    let nextServiceOrder = generalServiceRows.length
      ? Math.max(...generalServiceRows.map(service => Number(service.sort_order || 0))) + 10
      : 10

    try {
      const importableRows = generalPreview.filter(row => {
        const skipReason = generalServiceImportRowSkipReason(row)
        if (skipReason) {
          skipped.push({ name: row.name || 'Unnamed service', reason: skipReason })
          return false
        }
        return true
      })
      const exactCategoryReuseKeys = new Set()
      const matchedCategoryReuseKeys = new Set()
      const manualCategoryReuseKeys = new Set()
      const categoryReviewRows = []
      const matchedImportRows = importableRows.map(row => {
        const categoryKey = row.category_key || slugify(row.category_name || 'General Workshop') || 'general-workshop'
        const nextRow = {
          ...row,
          name: String(row.name || '').trim(),
          category_key: categoryKey,
          category_name: String(row.category_name || 'General Workshop').trim() || 'General Workshop',
        }
        if (nextRow.matched_category_id) {
          if (nextRow.category_match_type === 'manual') manualCategoryReuseKeys.add(nextRow.category_key)
          else if (nextRow.category_match_type === 'exact') exactCategoryReuseKeys.add(nextRow.category_key)
          else matchedCategoryReuseKeys.add(nextRow.category_key)
        }
        if (nextRow.category_needs_review) categoryReviewRows.push(nextRow)
        return nextRow
      })

      for (const row of matchedImportRows) {
        if (row.needs_review) reviewRows.push(row)
        if (row.matched_category_id) {
          const category = serviceCategories.find(item => item.id === row.matched_category_id)
          if (category) categoryByKey.set(category.key, category)
          continue
        }
        if (categoryByKey.has(row.category_key)) {
          reusedCategoryKeys.add(row.category_key)
          continue
        }

        const { data, error } = await supabase
          .from('service_categories')
          .insert({
            workshop_id: workshopId,
            module_key: 'general',
            key: row.category_key,
            name: row.category_name,
            sort_order: nextCategoryOrder,
            active: true,
          })
          .select('*')
          .single()
        if (error) throw error
        nextCategoryOrder += 10
        categoryByKey.set(row.category_key, data)
        createdCategories.push(data)
      }

      const serviceRows = []
      for (const row of matchedImportRows) {
        const category = categoryByKey.get(row.category_key)
        const duplicateKey = generalServiceImportDuplicateKey(row, category?.key || row.category_key)
        if (existingKeys.has(duplicateKey) || previewKeys.has(duplicateKey)) {
          skipped.push({ name: row.name, reason: 'Duplicate General service in this category.' })
          const existingService = existingServiceByKey.get(duplicateKey)
          if (existingService?.id && isPilotGeneralChecklistServiceName(existingService.name)) {
            assignmentCandidates.set(existingService.id, existingService)
          }
          continue
        }
        previewKeys.add(duplicateKey)
        serviceRows.push({
          workshop_id: workshopId,
          module_key: 'general',
          service_category_id: category?.id || null,
          category: category?.key || row.category_key || 'general-workshop',
          brand: 'any',
          name: row.name,
          description: row.description || '',
          internal_notes: [
            'Service Card setup required after import.',
            row.needs_review ? `AI import needs review: ${row.review_reasons.join(', ')}` : '',
          ].filter(Boolean).join(' '),
          pricing_mode: row.pricing_mode || 'fixed',
          base_price: Math.max(0, Number(row.base_price) || 0),
          visibility: 'archived',
          booking_type: row.booking_type || 'assessment_first',
          expected_labour_minutes: Math.max(0, Math.round(Number(row.expected_labour_minutes) || 0)),
          sort_order: nextServiceOrder,
          active: false,
          is_addon: row.booking_type === 'internal_addon',
        })
        nextServiceOrder += 10
      }

      let insertedServices = []
      if (serviceRows.length) {
        const { data, error } = await supabase
          .from('services')
          .insert(serviceRows)
          .select('id, name, module_key, service_category_id, category')
        if (error) throw error
        insertedServices = data || []
      }

      insertedServices
        .filter(service => service.module_key === 'general' && isPilotGeneralChecklistServiceName(service.name))
        .forEach(service => assignmentCandidates.set(service.id, service))
      const checklistAssignments = await ensurePilotChecklistAssignments(Array.from(assignmentCandidates.values()))
      setGeneralPreview(null)
      setGeneralPasteText('')
      setGeneralImportSummary({
        categoriesCreated: createdCategories.length,
        categoriesReused: exactCategoryReuseKeys.size + matchedCategoryReuseKeys.size + manualCategoryReuseKeys.size + reusedCategoryKeys.size,
        categoriesReusedExact: exactCategoryReuseKeys.size + reusedCategoryKeys.size,
        categoriesReusedMatched: matchedCategoryReuseKeys.size,
        categoriesReusedManual: manualCategoryReuseKeys.size,
        categoryReviewCount: categoryReviewRows.length,
        categoryMatches: matchedImportRows
          .filter(row => row.matched_category_id && row.category_match_type === 'matched')
          .map(row => ({
            serviceName: row.name,
            matchedCategory: row.category_name,
          })),
        servicesCreated: insertedServices.length,
        servicesSkipped: skipped.length,
        servicesSkippedByUser: skipped.filter(row => row.reason === 'Skipped by user.').length,
        reviewCount: reviewRows.length,
        skipped,
        reviewRows,
        checklistAssignments,
      })
      setSaveMsg('General services imported.')
      await load()
    } catch (err) {
      setGeneralParseError(`Could not import General services: ${err.message}`)
    } finally {
      setSaving(false)
      setTimeout(() => setSaveMsg(null), 3000)
    }
  }

  async function deleteService(id) {
    await supabase.from('services').delete().eq('id', id)
    await load()
  }

  async function saveWorkshop() {
    setSaving(true)
    const payload = {
      name: workshopForm.name,
      email: workshopForm.email,
      phone: workshopForm.phone,
      address: workshopForm.address,
      logo_url: workshopForm.logo_url,
      slug: workshopForm.slug ? slugify(workshopForm.slug) : null,
      max_jobs_per_day: Math.max(1, Number(workshopForm.max_jobs_per_day) || 4),
      booking_notification_email: workshopForm.booking_notification_email,
      invoice_prefix: workshopForm.invoice_prefix,
      bank_account_name: workshopForm.bank_account_name,
      bank_sort_code: workshopForm.bank_sort_code,
      bank_account_number: workshopForm.bank_account_number,
      reminders_enabled: workshopForm.reminders_enabled !== false,
      discount_approval_required: workshopForm.discount_approval_required === true,
      service_passport_enabled: workshopForm.service_passport_enabled === true,
      loyalty_enabled: workshopForm.loyalty_enabled === true,
      loyalty_required_completed_services: Math.max(1, Math.min(100, Number(workshopForm.loyalty_required_completed_services) || 4)),
      loyalty_reward_type: workshopForm.loyalty_reward_type === 'fixed' ? 'fixed' : 'percent',
      loyalty_reward_value: Math.max(0, Math.min(workshopForm.loyalty_reward_type === 'percent' ? 100 : 99999, Number(workshopForm.loyalty_reward_value) || 0)),
      loyalty_eligible_categories: normaliseLoyaltyCategories(workshopForm.loyalty_eligible_categories),
    }
    const { error } = await supabase.from('workshops').update(payload).eq('id', workshopId)
    if (!error) {
      await refreshWorkshop()
      await load()
    }
    setSaveMsg(error ? 'Error saving — check console.' : 'Saved.')
    setSaving(false)
    setTimeout(() => setSaveMsg(null), 3000)
  }

  function setCapacityMemberField(memberId, field, value) {
    setCapacityTeamForm(rows => rows.map(row => (
      row.id === memberId ? { ...row, [field]: value } : row
    )))
  }

  async function saveCapacitySettings() {
    if (!isOwner || !workshopId) return
    setCapacitySaving(true)
    setCapacityError('')
    setCapacityMsg(null)
    try {
      await runCapacitySettingsUpdate(supabase, workshopId, {
        weeklyBookableHours: workshopForm.weekly_bookable_hours,
        technicians: capacityTeamForm,
      })
      await refreshWorkshop()
      await load()
      setCapacityMsg('Calendar capacity saved.')
    } catch (err) {
      setCapacityError(err.message || 'Could not save calendar capacity.')
    } finally {
      setCapacitySaving(false)
      setTimeout(() => setCapacityMsg(null), 3000)
    }
  }

  async function uploadWorkshopLogo(event) {
    const file = event.target.files?.[0]
    if (event.target) event.target.value = ''
    if (!file) return

    setLogoUploadError('')
    setSaveMsg(null)

    if (!workshopId) {
      setLogoUploadError('Workshop is still loading. Try again in a moment.')
      return
    }

    const ext = LOGO_FILE_TYPES[file.type]
    if (!ext) {
      setLogoUploadError('Upload a PNG, JPEG, or WebP logo.')
      return
    }

    if (file.size > LOGO_MAX_FILE_SIZE) {
      setLogoUploadError('Logo file must be 2 MB or smaller.')
      return
    }

    setLogoUploading(true)
    try {
      const unique = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      const path = `${workshopId}/logo-${unique}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from(LOGO_BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false })
      if (uploadError) throw uploadError

      const { data } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path)
      if (!data?.publicUrl) throw new Error('Logo uploaded but no public URL was returned.')

      setWorkshopForm(f => ({ ...f, logo_url: data.publicUrl }))
      setSaveMsg('Logo uploaded. Save Details to publish it.')
    } catch (err) {
      setLogoUploadError(err.message || 'Logo upload failed.')
    } finally {
      setLogoUploading(false)
    }
  }

  function applyStripeConnectStatus(account = {}) {
    setWorkshopForm(f => ({
      ...f,
      stripe_connected_account_id: Object.prototype.hasOwnProperty.call(account, 'connected_account_id') ? account.connected_account_id || '' : f.stripe_connected_account_id || '',
      stripe_connect_account_type: Object.prototype.hasOwnProperty.call(account, 'account_type') ? account.account_type || '' : f.stripe_connect_account_type || '',
      stripe_connect_charges_enabled: !!account.charges_enabled,
      stripe_connect_payouts_enabled: !!account.payouts_enabled,
      stripe_connect_onboarding_complete: !!account.onboarding_complete,
      stripe_connect_disabled_reason: account.disabled_reason || '',
      stripe_connect_last_checked_at: account.last_checked_at || new Date().toISOString(),
    }))
  }

  async function stripeConnectRequest(action) {
    if (localTestMode) throw new Error('Stripe actions are disabled in the isolated local environment.')
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) throw new Error('Please sign in again before updating Stripe payments.')

    const res = await fetch('/.netlify/functions/stripe-connect-account', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action, workshopId }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Stripe Connect request failed.')
    return data
  }

  async function refreshStripeConnectStatus({ fromRedirect = false } = {}) {
    setConnectBusy('status')
    setConnectMsg(null)
    setConnectError(null)
    try {
      const data = await stripeConnectRequest('status')
      applyStripeConnectStatus(data.account)
      await refreshWorkshop()
      setConnectMsg(fromRedirect ? 'Stripe setup status refreshed.' : 'Stripe status refreshed.')
    } catch (err) {
      setConnectError(err.message)
    } finally {
      setConnectBusy('')
    }
  }

  async function startStripeConnect() {
    setConnectBusy('start')
    setConnectMsg(null)
    setConnectError(null)
    try {
      const data = await stripeConnectRequest('start')
      if (!data.url) throw new Error('Stripe did not return a connect URL.')
      window.location.href = data.url
    } catch (err) {
      setConnectError(err.message)
      setConnectBusy('')
    }
  }

  async function disconnectStripeConnect() {
    setConnectBusy('disconnect')
    setConnectMsg(null)
    setConnectError(null)
    try {
      const data = await stripeConnectRequest('disconnect')
      applyStripeConnectStatus(data.account)
      await refreshWorkshop()
      setConnectMsg('Stripe Standard account disconnected. Card payment links are disabled for new jobs.')
    } catch (err) {
      setConnectError(err.message)
    } finally {
      setConnectBusy('')
    }
  }

  function newGeneralCategory() {
    const nextOrder = serviceCategories.length
      ? Math.max(...serviceCategories.map(category => Number(category.sort_order || 0))) + 10
      : 10
    setEditingCategory({ name: '', sort_order: nextOrder, active: true })
  }

  function uniqueGeneralCategoryKey(name, categoryId) {
    const baseKey = slugify(name) || 'general-service'
    const existingKeys = new Set(
      serviceCategories
        .filter(category => category.id !== categoryId)
        .map(category => category.key)
    )
    let key = baseKey
    let suffix = 2
    while (existingKeys.has(key)) {
      key = `${baseKey}-${suffix}`
      suffix += 1
    }
    return key
  }

  async function saveGeneralCategory() {
    if (!editingCategory || !isOwner || workshopForm.general_workshop_enabled !== true) return
    const name = String(editingCategory.name || '').trim()
    if (!name) {
      setSaveMsg('Category name is required.')
      setTimeout(() => setSaveMsg(null), 3000)
      return
    }

    setSaving(true)
    const payload = {
      module_key: 'general',
      name,
      sort_order: Math.max(0, Math.round(Number(editingCategory.sort_order) || 0)),
      active: editingCategory.active !== false,
    }
    const result = editingCategory.id
      ? await supabase.from('service_categories').update(payload).eq('id', editingCategory.id)
      : await supabase.from('service_categories').insert({
        ...payload,
        workshop_id: workshopId,
        key: uniqueGeneralCategoryKey(name),
      })

    if (result.error) {
      setSaveMsg(`Error saving category: ${result.error.message}`)
    } else {
      setEditingCategory(null)
      setSaveMsg('Category saved.')
      await load()
    }
    setSaving(false)
    setTimeout(() => setSaveMsg(null), 3000)
  }

  async function setGeneralCategoryActive(category, active) {
    if (!category?.id || !isOwner || workshopForm.general_workshop_enabled !== true) return
    setSaving(true)
    const { error } = await supabase
      .from('service_categories')
      .update({ active })
      .eq('id', category.id)
    setSaveMsg(error ? `Error updating category: ${error.message}` : (active ? 'Category restored.' : 'Category archived.'))
    await load()
    setSaving(false)
    setTimeout(() => setSaveMsg(null), 3000)
  }

  async function deleteUnusedGeneralCategory(category) {
    if (!category?.id || !canDeleteGeneralRecords) return
    const confirmed = typeof window === 'undefined' || window.confirm(`Permanently delete "${category.name}"? Only unused General categories can be deleted.`)
    if (!confirmed) return

    setSaving(true)
    const { error } = await supabase.rpc('delete_unused_general_category', { p_category_id: category.id })
    setSaveMsg(error
      ? (error.message || 'This category is linked to services or history and cannot be deleted. Archive it instead.')
      : 'Category deleted.')
    await load()
    setSaving(false)
    setTimeout(() => setSaveMsg(null), 4000)
  }

  function newGeneralService() {
    const firstCategory = activeGeneralCategories[0] || null
    const nextOrder = generalServiceRows.length
      ? Math.max(...generalServiceRows.map(service => Number(service.sort_order || 0))) + 10
      : 10
    setEditingService({
      module_key: 'general',
      service_category_id: firstCategory?.id || null,
      category: firstCategory?.key || 'general',
      brand: 'any',
      name: '',
      description: '',
      internal_notes: '',
      pricing_mode: 'fixed',
      base_price: '',
      visibility: 'internal',
      booking_type: 'assessment_first',
      expected_labour_minutes: 0,
      sort_order: nextOrder,
      active: true,
      is_addon: false,
      service_card_selection: '',
    })
  }

  function editGeneralService(service) {
    const assignment = activeServiceCardAssignment(serviceCardAssignments, service.id)
    const template = assignment?.template || {}
    setEditingService({
      ...service,
      service_card_selection: serviceCardSelectionValue({
        blueprint_key: template.source_blueprint_key,
        version: template.source_blueprint_version,
      }),
    })
  }

  function generalServicePayload(service) {
    const category = serviceCategories.find(item => item.id === service.service_category_id)
    const pricingMode = GENERAL_PRICING_MODES.some(option => option.value === service.pricing_mode) ? service.pricing_mode : 'fixed'
    const visibility = GENERAL_VISIBILITY_OPTIONS.some(option => option.value === service.visibility) ? service.visibility : 'internal'
    const bookingType = GENERAL_BOOKING_TYPES.some(option => option.value === service.booking_type) ? service.booking_type : 'assessment_first'

    return {
      module_key: 'general',
      service_category_id: category?.id || null,
      category: category?.key || service.category || 'general',
      brand: 'any',
      name: String(service.name || '').trim(),
      description: service.description || '',
      internal_notes: service.internal_notes || '',
      pricing_mode: pricingMode,
      base_price: Math.max(0, Number(service.base_price) || 0),
      visibility,
      booking_type: bookingType,
      expected_labour_minutes: Math.max(0, Math.round(Number(service.expected_labour_minutes) || 0)),
      sort_order: Math.max(0, Math.round(Number(service.sort_order) || 0)),
      active: visibility !== 'archived',
      is_addon: bookingType === 'internal_addon',
    }
  }

  async function setGeneralServiceArchived(service, archived) {
    if (!service?.id || !isOwner || workshopForm.general_workshop_enabled !== true) return
    const visibility = archived ? 'archived' : 'internal'
    setSaving(true)
    const { error } = await supabase
      .from('services')
      .update({ module_key: 'general', visibility, active: !archived })
      .eq('id', service.id)
    setSaveMsg(error ? `Error updating service: ${error.message}` : (archived ? 'Service archived.' : 'Service restored.'))
    await load()
    setSaving(false)
    setTimeout(() => setSaveMsg(null), 3000)
  }

  async function deleteUnusedGeneralService(service) {
    if (!service?.id || !canDeleteGeneralRecords) return
    const confirmed = typeof window === 'undefined' || window.confirm(`Permanently delete "${service.name}"? Only unused General services can be deleted.`)
    if (!confirmed) return

    setSaving(true)
    const { error } = await supabase.rpc('delete_unused_general_service', { p_service_id: service.id })
    setSaveMsg(error
      ? (error.message || 'This service has job, booking, or checklist history and cannot be deleted. Archive it instead.')
      : 'Service deleted.')
    await load()
    setSaving(false)
    setTimeout(() => setSaveMsg(null), 4000)
  }

  async function saveService() {
    if (!editingService) return
    setSaving(true)
    const isGeneral = editingService.module_key === 'general'

    if (isGeneral) {
      const payload = generalServicePayload(editingService)
      if (!payload.name) {
        setSaveMsg('Service name is required.')
        setSaving(false)
        setTimeout(() => setSaveMsg(null), 3000)
        return
      }
      const cardSelection = parseServiceCardSelection(editingService.service_card_selection)
      if (payload.active && Number(payload.expected_labour_minutes || 0) <= 0) {
        setSaveMsg('Positive Expected Labour Minutes are required before activation.')
        setSaving(false)
        setTimeout(() => setSaveMsg(null), 4000)
        return
      }
      if (payload.active && !cardSelection.blueprintKey) {
        setSaveMsg('Select an approved Service Card before activation.')
        setSaving(false)
        setTimeout(() => setSaveMsg(null), 4000)
        return
      }
      const result = await supabase.rpc('save_general_service_with_card', {
        p_service_id: editingService.id || null,
        p_service: payload,
        p_blueprint_key: cardSelection.blueprintKey || null,
        p_blueprint_version: cardSelection.version,
      })
      if (result.error) {
        setSaveMsg(`Error saving service: ${result.error.message}`)
        setSaving(false)
        setTimeout(() => setSaveMsg(null), 3000)
        return
      }
    } else {
      const suspensionPayload = {
        ...editingService,
        expected_labour_minutes: Math.max(0, Math.round(Number(editingService.expected_labour_minutes) || 0)),
      }
      if (editingService.id) {
        await supabase.from('services').update(suspensionPayload).eq('id', editingService.id)
      } else {
        await supabase.from('services').insert({ ...suspensionPayload, workshop_id: workshopId })
      }
    }
    setEditingService(null)
    if (isGeneral) setSaveMsg('Service saved.')
    await load(); setSaving(false)
    if (isGeneral) setTimeout(() => setSaveMsg(null), 3000)
  }

  async function sendInvite(e) {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    if (localTestMode) {
      setInviteError('Team invitations are disabled in the isolated local environment.')
      return
    }
    setInviting(true); setInviteError(null); setInviteMsg(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before inviting a team member.')

      const res = await fetch('/.netlify/functions/invite-tech', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          workshopId
        })
      })
      const data = await res.json()
      if (data.error) throw new Error(data.error)
      setInviteMsg(`Invite sent to ${inviteEmail.trim()}.`)
      setInviteEmail('')
    } catch (err) {
      setInviteError(err.message)
    }
    setInviting(false)
  }

  async function removeMember(memberId) {
    // Deletes the profile — cascade removes their access. Auth user remains but can't log in to this workshop.
    await supabase.from('profiles').delete().eq('id', memberId)
    await load()
  }

  function changeTab(nextTab) {
    setTab(nextTab)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', nextTab)
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
    }
  }

  function toggleLoyaltyCategory(category) {
    setWorkshopForm(current => {
      const existing = normaliseLoyaltyCategories(current.loyalty_eligible_categories)
      const next = existing.includes(category)
        ? existing.filter(item => item !== category)
        : [...existing, category]
      return {
        ...current,
        loyalty_eligible_categories: next.length ? next : [...DEFAULT_LOYALTY_CATEGORIES],
      }
    })
  }

  async function copyText(text, message) {
    if (!text) return
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text)
      }
      setSaveMsg(message)
    } catch (err) {
      setSaveMsg('Could not copy automatically. Select the text and copy it manually.')
    }
    setTimeout(() => setSaveMsg(null), 3000)
  }

  const generalWorkshopEnabled = workshopForm.general_workshop_enabled === true
  const canManageGeneralServices = isOwner && generalWorkshopEnabled
  const canDeleteGeneralRecords = generalWorkshopEnabled && profile?.role === 'owner' && profile?.workshop_id === workshopId
  const suspensionServiceRows = suspensionServices(services)
  const generalServiceRows = generalServices(services)
  const activeGeneralCategories = serviceCategories.filter(category => category.active !== false).sort(sortByOrderThenName)
  const archivedGeneralCategories = serviceCategories.filter(category => category.active === false).sort(sortByOrderThenName)
  const generalCategoryById = new Map(serviceCategories.map(category => [category.id, category]))
  const visibleGeneralServiceRows = generalServiceRows
    .filter(service => service.active !== false && service.visibility !== 'archived')
    .sort(sortByOrderThenName)
  const archivedGeneralServiceRows = generalServiceRows
    .filter(service => service.active === false || service.visibility === 'archived')
    .sort(sortByOrderThenName)
  const generalServiceGroups = visibleGeneralServiceRows.reduce((groups, service) => {
    const category = generalCategoryById.get(service.service_category_id)
    const key = category?.id || service.category || 'uncategorised'
    let group = groups.find(item => item.key === key)
    if (!group) {
      group = {
        key,
        title: category?.name || humaniseCategory(service.category),
        services: [],
      }
      groups.push(group)
    }
    group.services.push(service)
    return groups
  }, [])
  const forkServices = suspensionServiceRows.filter(s => s.category === 'fork' && !s.is_addon)
  const shockServices = suspensionServiceRows.filter(s => s.category === 'shock' && !s.is_addon)
  const dropperServices = suspensionServiceRows.filter(s => s.category === 'dropper' && !s.is_addon)
  const brakeServices = suspensionServiceRows.filter(s => s.category === 'brake' && !s.is_addon)
  const combinedServices = suspensionServiceRows.filter(s => s.category === 'combined' && !s.is_addon)
  const otherServices = suspensionServiceRows.filter(s => !s.is_addon && !['fork', 'shock', 'dropper', 'brake', 'combined'].includes(s.category))
  const addons = suspensionServiceRows.filter(s => s.is_addon)

  if (loading && !loadedRef.current) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>
  if (loadError && !loadedRef.current) return (
    <div className="empty" role="alert">
      <p>{loadError}</p>
      <button type="button" className="btn btn-primary" onClick={load}>Retry</button>
    </div>
  )

  const tabs = isOwner
    ? ['services', 'workshop', 'payments', 'stock', 'kits', 'specs', 'import', 'team', 'subscription']
    : ['services']
  const tabDescriptions = {
    services: 'Suspension and General service menus',
    workshop: 'Identity, booking rules and access',
    payments: 'Transfers, card setup and safeguards',
    stock: 'Parts, pricing and physical usage',
    kits: 'Component-specific identification',
    specs: 'Workshop-verified reference data',
    import: 'Reviewed customer and history import',
    team: 'People, permissions and capacity',
    subscription: 'Plan and account access',
  }
  const bookingUrl = workshopForm.slug && typeof window !== 'undefined'
    ? `${window.location.origin}/book/${workshopForm.slug}`
    : ''
  const iframeCode = bookingUrl ? `<iframe src="${bookingUrl}?embed=1" style="width:100%;max-width:620px;height:520px;border:0;display:block;margin:24px auto"></iframe>` : ''
  const activeTab = tabs.includes(tab) ? tab : tabs[0]
  const activeServices = suspensionServiceRows.filter(s => s.active !== false)
  const showingGeneralServices = canManageGeneralServices && serviceMenuModule === 'general'
  const selectedGeneralCategory = editingService?.module_key === 'general'
    ? serviceCategories.find(category => category.id === editingService.service_category_id)
    : null
  const generalCategoryOptions = selectedGeneralCategory && !activeGeneralCategories.some(category => category.id === selectedGeneralCategory.id)
    ? [...activeGeneralCategories, selectedGeneralCategory]
    : activeGeneralCategories
  const hasBankDetails = Boolean(workshopForm.bank_account_name && workshopForm.bank_sort_code && workshopForm.bank_account_number)
  const stripeStandardReady = Boolean(
    workshopForm.stripe_connected_account_id
    && workshopForm.stripe_connect_charges_enabled
    && workshopForm.stripe_connect_payouts_enabled
    && workshopForm.stripe_connect_onboarding_complete
    && workshopForm.stripe_connect_disabled_reason !== 'stripe_express_deprecated'
  )
  const stripeStandardConnected = Boolean(
    workshopForm.stripe_connected_account_id
    && workshopForm.stripe_connect_account_type === 'standard'
  )
  const paymentReady = Boolean(hasBankDetails || stripeStandardReady)
  const launchSteps = [
    {
      key: 'details',
      title: 'Workshop details',
      desc: 'Name plus the email that receives booking notifications.',
      done: Boolean(workshopForm.name && (workshopForm.booking_notification_email || workshopForm.email)),
      tab: 'workshop',
      action: 'Edit details'
    },
    {
      key: 'services',
      title: 'Service menu',
      desc: 'At least one public service for customers to choose.',
      done: activeServices.length > 0,
      tab: 'services',
      action: activeServices.length ? 'Manage services' : 'Add services'
    },
    {
      key: 'booking',
      title: 'Booking page',
      desc: 'A booking slug so the public widget has a live address.',
      done: Boolean(workshopForm.slug),
      tab: 'workshop',
      action: 'Set slug'
    },
    {
      key: 'capacity',
      title: 'Daily capacity',
      desc: 'Maximum jobs per day used to suggest available dates.',
      done: Number(workshopForm.max_jobs_per_day || 0) > 0,
      tab: 'workshop',
      action: 'Set capacity'
    },
    {
      key: 'team',
      title: 'Team member',
      desc: 'At least one workshop user or technician for job assignment.',
      done: team.length > 0,
      tab: 'team',
      action: 'Invite team'
    },
    {
      key: 'payments',
      title: 'Payments',
      desc: 'Bank transfer details or Stripe Standard card payments are ready.',
      done: paymentReady,
      tab: 'payments',
      action: 'Set payments'
    },
    {
      key: 'kits',
      title: 'Kit library',
      desc: 'Import common service kits so jobs can recommend parts.',
      done: launchStats.kits > 0,
      tab: 'kits',
      action: 'Import kits'
    },
    {
      key: 'first-job',
      title: 'First customer/job',
      desc: 'Create at least one real or demo job to test the workflow.',
      done: launchStats.jobs > 0,
      tab: 'services',
      action: 'Use app flow'
    },
    {
      key: 'sample-report',
      title: 'Sample report',
      desc: 'Save or email one report version so the workshop sees the customer output.',
      done: launchStats.reports > 0,
      tab: 'services',
      action: 'Create report'
    }
  ]
  const launchDoneCount = launchSteps.filter(step => step.done).length
  const launchChecklistComplete = launchDoneCount === launchSteps.length
  const showLaunchChecklist = isOwner && !launchChecklistHidden

  function hideLaunchChecklist() {
    if (!workshopId || typeof window === 'undefined') return
    window.localStorage.setItem(launchChecklistKey(workshopId), 'true')
    setLaunchChecklistHidden(true)
  }

  function showHiddenLaunchChecklist() {
    if (!workshopId || typeof window === 'undefined') return
    window.localStorage.removeItem(launchChecklistKey(workshopId))
    setLaunchChecklistHidden(false)
  }

  return (
    <div className="settings-refresh-page">
      <div className="settings-refresh-head">
        <div>
          <div className="page-kicker">Workshop administration</div>
          <h1>Workshop setup</h1>
          <p>Configure the existing service, scheduling, payment and access controls.</p>
        </div>
      </div>

      <div className="settings-refresh-grid" aria-label="Workshop setup sections">
        {tabs.map(t => (
          <button key={t} onClick={() => changeTab(t)}
            className={`settings-refresh-tab ${activeTab === t ? 'active' : ''}`}
            aria-current={activeTab === t ? 'page' : undefined}>
            <span>{t}</span>
            <small>{tabDescriptions[t]}</small>
          </button>
        ))}
      </div>

      {localTestMode && (
        <div className="alert alert-info settings-local-notice">
          Local isolation is active. Stripe, team invitations and AI parsing are unavailable; database-backed setup remains testable.
        </div>
      )}

      {loadError && loadedRef.current && (
        <div className="alert alert-error refresh-inline-error" role="alert">
          <span>{loadError} Existing setup values have been kept.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={load}>Retry</button>
        </div>
      )}

      {saveMsg && <div className="alert alert-success" style={{ marginBottom: 16 }}>{saveMsg}</div>}

      {isOwner && launchChecklistHidden && (
        <div className="action-row" style={{ justifyContent: 'flex-start', marginBottom: 16 }}>
          <span className="text-muted fs12">Workshop launch checklist hidden.</span>
          <button className="btn btn-ghost btn-sm" onClick={showHiddenLaunchChecklist}>Show Checklist</button>
        </div>
      )}

      {showLaunchChecklist && (
        <div className="card compact-card" style={{ marginBottom: 20, borderColor: launchChecklistComplete ? 'var(--green)' : 'var(--yellow-line)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 14 }}>
            <div>
              <div className="card-title" style={{ marginBottom: 4 }}>Workshop Launch Checklist</div>
              <div className="text-muted fs12">Finish these items before sharing the booking widget with customers.</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: launchChecklistComplete ? 'var(--green)' : 'var(--yellow)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
                {launchDoneCount}/{launchSteps.length} ready
              </div>
              {launchChecklistComplete && (
                <button className="btn btn-ghost btn-sm" onClick={hideLaunchChecklist}>Hide Checklist</button>
              )}
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)' }}>
            {launchSteps.map(step => (
              <div key={step.key} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '11px 0',
                borderBottom: '1px solid var(--border)'
              }}>
                <span style={{
                  width: 58,
                  flexShrink: 0,
                  textAlign: 'center',
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  color: step.done ? 'var(--green)' : 'var(--yellow)'
                }}>
                  {step.done ? 'Done' : 'Todo'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="text-white fw7 fs12">{step.title}</div>
                  <div className="text-muted fs12">{step.desc}</div>
                </div>
                {!step.done && (
                  <button className="btn btn-ghost btn-sm" onClick={() => changeTab(step.tab)}>
                    {step.action}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 14 }}>
            {bookingUrl ? (
              <>
                <button className="btn btn-primary btn-sm" onClick={() => copyText(bookingUrl, 'Booking link copied.')}>Copy Booking Link</button>
                <button className="btn btn-ghost btn-sm" onClick={() => copyText(iframeCode, 'Shopify iframe copied.')}>Copy Shopify Iframe</button>
                <a className="btn btn-ghost btn-sm" href={bookingUrl} target="_blank" rel="noopener noreferrer">Open Booking Page</a>
              </>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => changeTab('workshop')}>Set Booking Slug</button>
            )}
            <span className="text-muted fs12">
              {bookingUrl ? bookingUrl : 'Your booking link appears here once the slug is saved.'}
            </span>
          </div>
        </div>
      )}

      {/* ── SERVICES TAB ── */}
      {activeTab === 'services' && (
        <>
          {canManageGeneralServices && (
            <div className="settings-tabs" style={{ marginBottom: 16 }}>
              <button
                className={`btn ${serviceMenuModule === 'suspension' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                onClick={() => setServiceMenuModule('suspension')}
              >
                Suspension Services
              </button>
              <button
                className={`btn ${serviceMenuModule === 'general' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                onClick={() => setServiceMenuModule('general')}
              >
                General Workshop Services
              </button>
            </div>
          )}

          {!showingGeneralServices && (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div>
                    <div className="card-title" style={{ marginBottom: 2 }}>AI Service Menu Import</div>
                    <div className="text-muted fs12">Paste your existing service menu — prices, descriptions, anything. AI will extract and structure it.</div>
                  </div>
                </div>
                <textarea value={pasteText} onChange={e => setPasteText(e.target.value)} placeholder="Paste your service menu here — from your website, a Word doc, an email, anything…" style={{ minHeight: 140, marginBottom: 10 }} />
                {parseError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{parseError}</div>}
                <button className="btn btn-primary" onClick={parseServices} disabled={localTestMode || parsing || !pasteText.trim()} title={localTestMode ? 'Disabled in isolated local testing' : undefined}>
                  {parsing ? <><span className="spinner" />&nbsp;Parsing…</> : 'Parse with AI'}
                </button>
              </div>

              {preview && (
                <div className="card" style={{ marginBottom: 16 }}>
                  <div className="card-title">Preview — {preview.length} services found</div>
                  <div style={{ marginBottom: 14 }} className="text-muted fs12">Check these look right before importing.</div>
                  <table style={{ marginBottom: 14 }}>
                    <thead><tr><th>Category</th><th>Brand</th><th>Name</th><th>Price</th><th>Add-on?</th></tr></thead>
                    <tbody>
                      {preview.map((s, i) => (
                        <tr key={i}>
                          <td style={{ textTransform: 'capitalize' }}>{s.category}</td>
                          <td style={{ textTransform: 'capitalize' }}>{s.brand}</td>
                          <td className="text-white">{s.name}</td>
                          <td className="text-green fw7">£{Number(s.base_price).toFixed(2)}</td>
                          <td>{s.is_addon ? 'Yes' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-green" onClick={importServices} disabled={saving}>{saving ? 'Importing…' : 'Import All'}</button>
                    <button className="btn btn-ghost" onClick={() => setPreview(null)}>Cancel</button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Your Service Menu</div>
                <button className="btn btn-primary btn-sm" onClick={() => setEditingService({ category: 'fork', brand: 'any', name: '', description: '', base_price: '', expected_labour_minutes: 0, is_addon: false })}>+ Add Service</button>
              </div>

              {suspensionServiceRows.length === 0 ? (
                <div className="empty"><div className="empty-icon">🔧</div><p>No services yet. Use AI import above or add manually.</p></div>
              ) : (
                [['Fork Services', forkServices], ['Shock Services', shockServices], ['Dropper Services', dropperServices], ['Brake Services', brakeServices], ['Combined Services', combinedServices], ['Other Services', otherServices], ['Add-ons & Upgrades', addons]].map(([title, list]) =>
                  list.length > 0 ? (
                    <div key={title} style={{ marginBottom: 20 }}>
                      <div className="service-group-head">
                        <div className="sec" style={{ marginTop: 0, marginBottom: 0 }}>{title}</div>
                        <span className="section-meta">{list.length} item{list.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table>
                          <thead><tr><th>Name</th><th>Brand</th><th>Price</th><th>Labour</th><th>Description</th><th></th></tr></thead>
                          <tbody>
                            {list.map(s => (
                              <tr key={s.id}>
                                <td className="text-white fw7">{s.name}</td>
                                <td style={{ textTransform: 'capitalize' }}>{s.brand === 'any' ? 'All brands' : s.brand}</td>
                                <td className="text-green fw7">£{Number(s.base_price).toFixed(2)}</td>
                                <td>{Number(s.expected_labour_minutes || 0)} min</td>
                                <td className="text-muted fs12">{s.description?.slice(0, 60)}{s.description?.length > 60 ? '…' : ''}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingService(s)}>Edit</button>
                                  {' '}
                                  <button className="btn btn-red btn-sm" onClick={() => deleteService(s.id)}>Delete</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null
                )
              )}
            </>
          )}

          {showingGeneralServices && (
            <>
              <div className="alert alert-info">
                General services can be configured here, but public booking support will be enabled in a later release.
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div>
                    <div className="card-title" style={{ marginBottom: 2 }}>AI General Service Menu Import</div>
                    <div className="text-muted fs12">Paste a General Workshop service menu. Imported rows stay internal until you review and publish them.</div>
                  </div>
                </div>
                <textarea
                  value={generalPasteText}
                  onChange={e => setGeneralPasteText(e.target.value)}
                  placeholder="Paste General Workshop services here — whole-bike servicing, brakes, drivetrain, e-bike checks, labour menu items…"
                  style={{ minHeight: 140, marginBottom: 10 }}
                />
                {generalParseError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{generalParseError}</div>}
                <button className="btn btn-primary" onClick={parseGeneralServices} disabled={localTestMode || generalParsing || !generalPasteText.trim()} title={localTestMode ? 'Disabled in isolated local testing' : undefined}>
                  {generalParsing ? <><span className="spinner" />&nbsp;Parsing…</> : 'Parse General Menu with AI'}
                </button>
              </div>

              {generalPreview && (
                <div className="card" style={{ marginBottom: 16 }}>
                  <div className="card-title">General Preview — {generalPreview.length} services found</div>
                  <div style={{ marginBottom: 14 }} className="text-muted fs12">Check and edit these before importing. The category shown here is the one Rebound will use.</div>
                  <table style={{ marginBottom: 14 }}>
                    <thead><tr><th>Import</th><th>Category</th><th>Name</th><th>Price</th><th>Visibility</th><th>Booking</th><th>Description</th><th>Review</th></tr></thead>
                    <tbody>
                      {generalPreview.map((service, i) => {
                        const lockedSkip = service.skip && service.skip_reason !== 'Missing service name.'
                        const importable = isGeneralServiceImportRowIncluded(service)
                        return (
                          <tr key={`${service.source_index}-${i}`}>
                            <td>
                              <label style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-start' }}>
                                <input
                                  type="checkbox"
                                  checked={service.include !== false && !service.skip}
                                  disabled={lockedSkip}
                                  onChange={e => updateGeneralPreviewRow(i, { include: e.target.checked })}
                                />
                                <span>{service.include !== false && !service.skip ? 'Include' : 'Skip'}</span>
                              </label>
                            </td>
                            <td style={{ minWidth: 190 }}>
                              <select
                                value={service.category_selection || service.matched_category_id || GENERAL_PREVIEW_NEW_CATEGORY}
                                disabled={lockedSkip}
                                onChange={e => updateGeneralPreviewCategory(i, e.target.value)}
                              >
                                {activeGeneralCategories.map(category => (
                                  <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                                <option value={GENERAL_PREVIEW_NEW_CATEGORY}>Create new category…</option>
                              </select>
                              {(service.category_selection || service.matched_category_id || GENERAL_PREVIEW_NEW_CATEGORY) === GENERAL_PREVIEW_NEW_CATEGORY && (
                                <input
                                  style={{ marginTop: 6 }}
                                  value={service.custom_category_name || ''}
                                  disabled={lockedSkip}
                                  onChange={e => updateGeneralPreviewCustomCategory(i, e.target.value)}
                                  placeholder="New category name"
                                />
                              )}
                              {service.raw_category_name && service.raw_category_name !== service.category_name && (
                                <div className="text-muted fs11" style={{ marginTop: 4 }}>Parsed as {service.raw_category_name}</div>
                              )}
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <input
                                value={service.name || ''}
                                disabled={lockedSkip}
                                onChange={e => updateGeneralPreviewName(i, e.target.value)}
                                placeholder="Service name"
                              />
                            </td>
                            <td style={{ minWidth: 130 }}>
                              <select
                                value={service.pricing_mode || 'fixed'}
                                disabled={lockedSkip}
                                onChange={e => updateGeneralPreviewRow(i, { pricing_mode: e.target.value })}
                              >
                                {GENERAL_PRICING_MODES.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={service.base_price ?? 0}
                                disabled={lockedSkip || service.pricing_mode === 'quote_required'}
                                onChange={e => updateGeneralPreviewRow(i, { base_price: e.target.value })}
                                style={{ marginTop: 6 }}
                              />
                              <div className="text-green fw7 fs11" style={{ marginTop: 4 }}>{importPriceLabel(service)}</div>
                            </td>
                            <td style={{ minWidth: 150 }}>
                              <select
                                value={service.visibility || 'internal'}
                                disabled={lockedSkip}
                                onChange={e => updateGeneralPreviewRow(i, { visibility: e.target.value })}
                              >
                                {GENERAL_VISIBILITY_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ minWidth: 160 }}>
                              <select
                                value={service.booking_type || 'assessment_first'}
                                disabled={lockedSkip}
                                onChange={e => updateGeneralPreviewRow(i, { booking_type: e.target.value })}
                              >
                                {GENERAL_BOOKING_TYPES.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </td>
                            <td style={{ minWidth: 220 }}>
                              <textarea
                                value={service.description || ''}
                                disabled={lockedSkip}
                                onChange={e => updateGeneralPreviewRow(i, { description: e.target.value })}
                                placeholder="Customer-facing description"
                                style={{ minHeight: 72 }}
                              />
                            </td>
                            <td>
                              {lockedSkip ? (
                                <StatusPill tone="orange">Skipped</StatusPill>
                              ) : importable ? (
                                service.needs_review ? (
                                  <StatusPill tone="blue">Review</StatusPill>
                                ) : (
                                  <StatusPill tone="green">Ready</StatusPill>
                                )
                              ) : (
                                <StatusPill tone="orange">Skipped</StatusPill>
                              )}
                              {(generalServiceImportRowSkipReason(service) || service.review_reasons?.length > 0) && (
                                <div className="text-muted fs11" style={{ marginTop: 4 }}>
                                  {generalServiceImportRowSkipReason(service) || service.review_reasons.join(', ')}
                                </div>
                              )}
                              {service.category_match_type === 'matched' && (
                                <div className="text-muted fs11" style={{ marginTop: 4 }}>Matched category</div>
                              )}
                              {service.category_match_type === 'manual' && (
                                <div className="text-muted fs11" style={{ marginTop: 4 }}>Manual category</div>
                              )}
                              {service.is_pilot_checklist_service && (
                                <div className="text-muted fs11" style={{ marginTop: 4 }}>Whole-Bike Service Card match</div>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-green" onClick={importGeneralServices} disabled={saving}>{saving ? 'Importing…' : 'Import General Services'}</button>
                    <button className="btn btn-ghost" onClick={() => setGeneralPreview(null)}>Cancel</button>
                  </div>
                </div>
              )}

              {generalImportSummary && (
                <div className="alert alert-success" style={{ marginBottom: 16 }}>
                  <div className="fw7" style={{ marginBottom: 4 }}>General import complete.</div>
                  <div className="fs12">
                    Categories created: {generalImportSummary.categoriesCreated} · reused exact: {generalImportSummary.categoriesReusedExact ?? generalImportSummary.categoriesReused} · reused matched: {generalImportSummary.categoriesReusedMatched || 0} · category review: {generalImportSummary.categoryReviewCount || 0} · services created: {generalImportSummary.servicesCreated} · skipped: {generalImportSummary.servicesSkipped} · rows needing review: {generalImportSummary.reviewCount}
                  </div>
                  {generalImportSummary.categoryMatches?.length > 0 && (
                    <div className="fs12" style={{ marginTop: 6 }}>
                      Matched categories: {generalImportSummary.categoryMatches.map(row => `${row.serviceName} → ${row.matchedCategory}`).join('; ')}
                    </div>
                  )}
                  {generalImportSummary.checklistAssignments && (
                    <div className="fs12" style={{ marginTop: 6 }}>
                      Whole-Bike Service Card assignments: created {generalImportSummary.checklistAssignments.created}, already present {generalImportSummary.checklistAssignments.alreadyPresent}, reactivated {generalImportSummary.checklistAssignments.reactivated}, needs review {generalImportSummary.checklistAssignments.failed}.
                    </div>
                  )}
                  {generalImportSummary.checklistAssignments?.failures?.length > 0 && (
                    <div className="fs12" style={{ marginTop: 6 }}>
                      Checklist review: {generalImportSummary.checklistAssignments.failures.map(row => `${row.serviceName} (${row.message})`).join('; ')}
                    </div>
                  )}
                  {generalImportSummary.skipped?.length > 0 && (
                    <div className="fs12" style={{ marginTop: 6 }}>
                      Skipped: {generalImportSummary.skipped.map(row => `${row.name} (${row.reason})`).join('; ')}
                    </div>
                  )}
                </div>
              )}

              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
                  <div>
                    <div className="card-title" style={{ marginBottom: 2 }}>General Categories</div>
                    <div className="text-muted fs12">Create workshop-specific categories for the General Workshop service menu.</div>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={newGeneralCategory}>+ Add Category</button>
                </div>

                {serviceCategories.length === 0 ? (
                  <div className="empty" style={{ marginBottom: 0 }}>
                    <p>No General categories yet.</p>
                  </div>
                ) : (
                  <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 0 }}>
                    <table>
                      <thead><tr><th>Name</th><th>Key</th><th>Order</th><th>Status</th><th></th></tr></thead>
                      <tbody>
                        {[...activeGeneralCategories, ...archivedGeneralCategories].map(category => (
                          <tr key={category.id}>
                            <td className="text-white fw7">{category.name}</td>
                            <td className="mono">{category.key}</td>
                            <td>{category.sort_order || 0}</td>
                            <td>
                              <StatusPill tone={category.active === false ? 'muted' : 'green'}>
                                {category.active === false ? 'Archived' : 'Active'}
                              </StatusPill>
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => setEditingCategory(category)}>Edit</button>
                              {' '}
                              <button className="btn btn-ghost btn-sm" onClick={() => setGeneralCategoryActive(category, category.active === false)}>
                                {category.active === false ? 'Restore' : 'Archive'}
                              </button>
                              {canDeleteGeneralRecords && (
                                <>
                                  {' '}
                                  <button className="btn btn-red btn-sm" onClick={() => deleteUnusedGeneralCategory(category)}>Delete</button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>General Workshop Services</div>
                <button className="btn btn-primary btn-sm" onClick={newGeneralService}>+ Add General Service</button>
              </div>

              {generalServiceRows.length === 0 ? (
                <div className="empty"><p>No General services yet. Add categories above, then create workshop-specific services.</p></div>
              ) : (
                <>
                  {generalServiceGroups.map(group => (
                    <div key={group.key} style={{ marginBottom: 20 }}>
                      <div className="service-group-head">
                        <div className="sec" style={{ marginTop: 0, marginBottom: 0 }}>{group.title}</div>
                        <span className="section-meta">{group.services.length} item{group.services.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table>
                          <thead><tr><th>Name</th><th>Service Card</th><th>Pricing</th><th>Visibility</th><th>Booking</th><th>Labour</th><th></th></tr></thead>
                          <tbody>
                            {group.services.map(service => (
                              <tr key={service.id}>
                                <td>
                                  <div className="text-white fw7">{service.name}</div>
                                  {service.description && <div className="text-muted fs12">{service.description.slice(0, 80)}{service.description.length > 80 ? '…' : ''}</div>}
                                </td>
                                <td>
                                  {(() => {
                                    const assignment = activeServiceCardAssignment(serviceCardAssignments, service.id)
                                    const issues = serviceSetupIssues(service, assignment)
                                    return (
                                      <>
                                        <div className={issues.length ? 'text-yellow fw7' : 'text-white'}>
                                          {assignment?.template?.name || 'Setup required'}
                                        </div>
                                        {issues.length > 0 && <div className="text-yellow fs12">{issues.join(' · ')}</div>}
                                      </>
                                    )
                                  })()}
                                </td>
                                <td className="text-green fw7">{formatGeneralPrice(service)}</td>
                                <td>{optionLabel(GENERAL_VISIBILITY_OPTIONS, service.visibility, 'Internal/workshop-only')}</td>
                                <td>{optionLabel(GENERAL_BOOKING_TYPES, service.booking_type, 'Assessment-first')}</td>
                                <td>{Number(service.expected_labour_minutes || 0)} min</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <button className="btn btn-ghost btn-sm" onClick={() => editGeneralService(service)}>Edit</button>
                                  {' '}
                                  <button className="btn btn-ghost btn-sm" onClick={() => setGeneralServiceArchived(service, true)}>Archive</button>
                                  {canDeleteGeneralRecords && (
                                    <>
                                      {' '}
                                      <button className="btn btn-red btn-sm" onClick={() => deleteUnusedGeneralService(service)}>Delete</button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}

                  {archivedGeneralServiceRows.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div className="service-group-head">
                        <div className="sec" style={{ marginTop: 0, marginBottom: 0 }}>Archived / Hidden</div>
                        <span className="section-meta">{archivedGeneralServiceRows.length} item{archivedGeneralServiceRows.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table>
                          <thead><tr><th>Name</th><th>Category</th><th>Pricing</th><th>Booking</th><th></th></tr></thead>
                          <tbody>
                            {archivedGeneralServiceRows.map(service => {
                              const category = generalCategoryById.get(service.service_category_id)
                              return (
                                <tr key={service.id}>
                                  <td className="text-white fw7">{service.name}</td>
                                  <td>{category?.name || humaniseCategory(service.category)}</td>
                                  <td className="text-green fw7">{formatGeneralPrice(service)}</td>
                                  <td>{optionLabel(GENERAL_BOOKING_TYPES, service.booking_type, 'Assessment-first')}</td>
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    <button className="btn btn-ghost btn-sm" onClick={() => editGeneralService(service)}>Edit</button>
                                    {' '}
                                    <button className="btn btn-green btn-sm" onClick={() => setGeneralServiceArchived(service, false)}>Restore</button>
                                    {canDeleteGeneralRecords && (
                                      <>
                                        {' '}
                                        <button className="btn btn-red btn-sm" onClick={() => deleteUnusedGeneralService(service)}>Delete</button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ── WORKSHOP TAB ── */}
      {activeTab === 'workshop' && process.env.REACT_APP_VAT_BUILD1 === 'true' && <WorkshopVatSettings key={workshopId} profile={profile} workshopId={workshopId} />}
      {activeTab === 'workshop' && isOwner && (
        <div className="card" style={{ maxWidth: 600 }}>
          <div className="card-title">Workshop Details</div>
          <div className="field"><label>Workshop Name</label><input value={workshopForm.name} onChange={e => setWorkshopForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="field"><label>Contact Email</label><input type="email" value={workshopForm.email} onChange={e => setWorkshopForm(f => ({ ...f, email: e.target.value }))} placeholder="service@yourworkshop.co.uk" /></div>
          <div className="field"><label>Booking Notification Email</label><input type="email" value={workshopForm.booking_notification_email} onChange={e => setWorkshopForm(f => ({ ...f, booking_notification_email: e.target.value.trim() }))} placeholder="bookings@yourworkshop.co.uk" /></div>
          <div className="field"><label>Phone</label><input value={workshopForm.phone} onChange={e => setWorkshopForm(f => ({ ...f, phone: e.target.value }))} /></div>
          <div className="field">
            <label>Logo URL</label>
            <div className="workshop-logo-field">
              <input
                type="url"
                value={workshopForm.logo_url}
                onChange={e => {
                  setLogoUploadError('')
                  setWorkshopForm(f => ({ ...f, logo_url: e.target.value }))
                }}
                placeholder="https://cdn.shopify.com/.../logo.png"
              />
              <label className={`btn btn-ghost workshop-logo-upload ${logoUploading ? 'disabled' : ''}`}>
                {logoUploading ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={uploadWorkshopLogo}
                  disabled={logoUploading || saving}
                />
              </label>
            </div>
            <div className="field-hint">PNG, JPEG or WebP. Max 2 MB. Uploaded logos still use this URL field.</div>
          </div>
          {logoUploadError && <div className="alert alert-error">{logoUploadError}</div>}
          {workshopForm.logo_url && (
            <div className="workshop-logo-preview">
              <img src={workshopForm.logo_url} alt="Workshop logo preview" />
              <span>Logo preview</span>
            </div>
          )}
          <div className="field"><label>Address</label><textarea value={workshopForm.address} onChange={e => setWorkshopForm(f => ({ ...f, address: e.target.value }))} style={{ minHeight: 80 }} /></div>
          <div className="grid-2">
            <div className="field">
              <label>Booking Slug</label>
              <input
                value={workshopForm.slug}
                onChange={e => setWorkshopForm(f => ({ ...f, slug: slugify(e.target.value) }))}
                placeholder="your-workshop"
              />
            </div>
            <div className="field">
              <label>Max Jobs Per Day</label>
              <input
                type="number"
                min="1"
                value={workshopForm.max_jobs_per_day}
                onChange={e => setWorkshopForm(f => ({ ...f, max_jobs_per_day: e.target.value }))}
              />
            </div>
          </div>
          {bookingUrl && (
            <div className="field">
              <label>Booking Iframe</label>
              <input readOnly value={iframeCode} />
            </div>
          )}
          <div className="job-modal-callout" style={{ marginBottom: 16 }}>
            <div className="job-modal-callout-title">Suspension Service Passport</div>
            <div className="text-muted fs12" style={{ marginBottom: 12 }}>
              Let customers download a minimal suspension service passport from their rider portal. Only completed and paid fork, shock, and dropper services are included.
            </div>
            <label className="check-item">
              <input
                type="checkbox"
                checked={workshopForm.service_passport_enabled === true}
                onChange={e => setWorkshopForm(f => ({ ...f, service_passport_enabled: e.target.checked }))}
              />
              <span>Enable customer Service Passport downloads</span>
            </label>
            <div className="text-muted fs11" style={{ marginTop: 8 }}>
              Passport entries show component type, brand, model, serial, service name/type, service date, and explicit add-on upgrade names only.
            </div>
          </div>
          <button className="btn btn-primary" onClick={saveWorkshop} disabled={saving}>{saving ? 'Saving…' : 'Save Details'}</button>
        </div>
      )}

      {/* ── PAYMENTS TAB ── */}
      {activeTab === 'payments' && isOwner && (
        <>
          {profile?.role === 'owner' && profile?.workshop_id === workshopId && <ReviewSettings key={workshopId} workshopId={workshopId} />}
          <div className="card" style={{ maxWidth: 600, marginBottom: 20 }}>
            <div className="card-title">Service Reminders</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>When enabled, Rebound automatically emails customers 14 days before their next service date is due.</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <input
                type="checkbox"
                id="reminders_enabled"
                checked={workshopForm.reminders_enabled !== false}
                onChange={e => setWorkshopForm(f => ({ ...f, reminders_enabled: e.target.checked }))}
                style={{ width: 18, height: 18, accentColor: 'var(--orange)', cursor: 'pointer' }}
              />
              <label htmlFor="reminders_enabled" style={{ margin: 0, textTransform: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer', color: 'var(--white)' }}>
                Enable automatic service reminder emails
              </label>
            </div>
            <button className="btn btn-primary" onClick={saveWorkshop} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>

          <div className="card" style={{ maxWidth: 600, marginBottom: 20 }}>
            <div className="card-title">Invoice Numbering</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>Set a short prefix for your invoice numbers. E.g. "TC" produces TC-2026-0001.</div>
            <div className="field">
              <label>Invoice Prefix</label>
              <input
                value={workshopForm.invoice_prefix}
                onChange={e => setWorkshopForm(f => ({ ...f, invoice_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) }))}
                placeholder="TC"
                style={{ maxWidth: 120 }}
              />
            </div>
            <button className="btn btn-primary" onClick={saveWorkshop} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>

          <div className="card" style={{ maxWidth: 600, marginBottom: 20 }}>
            <div className="card-title">Discount Approval</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>
              Require the workshop owner to approve discounts and positive adjustments before they affect payable totals.
            </div>
            <label className="check-item" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={workshopForm.discount_approval_required === true}
                onChange={e => setWorkshopForm(f => ({ ...f, discount_approval_required: e.target.checked }))}
              />
              <span>Require owner approval for discounts and adjustments</span>
            </label>
            <button className="btn btn-primary" onClick={saveWorkshop} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>

          <div className="card" style={{ maxWidth: 680, marginBottom: 20 }}>
            <div className="card-title">Loyalty Rewards</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>
              Optional workshop loyalty for completed and paid eligible service jobs. Rewards apply as normal job-level discounts.
            </div>
            <label className="check-item" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={workshopForm.loyalty_enabled === true}
                onChange={e => setWorkshopForm(f => ({
                  ...f,
                  loyalty_enabled: e.target.checked,
                  loyalty_required_completed_services: f.loyalty_required_completed_services || 4,
                  loyalty_reward_type: f.loyalty_reward_type || 'percent',
                  loyalty_reward_value: f.loyalty_reward_value || 20,
                  loyalty_eligible_categories: normaliseLoyaltyCategories(f.loyalty_eligible_categories),
                }))}
              />
              <span>Enable loyalty rewards</span>
            </label>

            <div className="grid-3" style={{ marginBottom: 12 }}>
              <div className="field">
                <label>Completed Paid Services</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={workshopForm.loyalty_required_completed_services}
                  onChange={e => setWorkshopForm(f => ({ ...f, loyalty_required_completed_services: e.target.value }))}
                />
              </div>
              <div className="field">
                <label>Reward Type</label>
                <select
                  value={workshopForm.loyalty_reward_type}
                  onChange={e => setWorkshopForm(f => ({ ...f, loyalty_reward_type: e.target.value }))}
                >
                  <option value="percent">Percentage</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </div>
              <div className="field">
                <label>{workshopForm.loyalty_reward_type === 'fixed' ? 'Reward GBP' : 'Reward %'}</label>
                <input
                  type="number"
                  min="0"
                  max={workshopForm.loyalty_reward_type === 'percent' ? '100' : undefined}
                  step="0.01"
                  value={workshopForm.loyalty_reward_value}
                  onChange={e => setWorkshopForm(f => ({ ...f, loyalty_reward_value: e.target.value }))}
                />
              </div>
            </div>

            <div className="field">
              <label>Eligible Categories</label>
              <div className="action-row">
                {LOYALTY_CATEGORY_OPTIONS.map(([category, label]) => {
                  const checked = normaliseLoyaltyCategories(workshopForm.loyalty_eligible_categories).includes(category)
                  return (
                    <button
                      key={category}
                      type="button"
                      className={`btn ${checked ? 'btn-primary' : 'btn-ghost'} btn-sm`}
                      onClick={() => toggleLoyaltyCategory(category)}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
              <div className="text-muted fs11" style={{ marginTop: 6 }}>
                Suggested launch setup: Fork, Shock and Combined fork/shock services.
              </div>
            </div>

            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              Current rule: {loyaltyRuleSummary(workshopForm)}.
            </div>
            <button className="btn btn-primary" onClick={saveWorkshop} disabled={saving}>{saving ? 'Saving...' : 'Save Loyalty'}</button>
          </div>

          <div className="card" style={{ maxWidth: 600, marginBottom: 20 }}>
            <div className="card-title">Bank Transfer Details</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>These appear on service reports so customers can pay by bank transfer.</div>
            <div className="field"><label>Account Name</label><input value={workshopForm.bank_account_name} onChange={e => setWorkshopForm(f => ({ ...f, bank_account_name: e.target.value }))} placeholder="Torque Collective Ltd" /></div>
            <div className="field"><label>Sort Code</label><input value={workshopForm.bank_sort_code} onChange={e => setWorkshopForm(f => ({ ...f, bank_sort_code: e.target.value }))} placeholder="00-00-00" style={{ maxWidth: 140 }} /></div>
            <div className="field"><label>Account Number</label><input value={workshopForm.bank_account_number} onChange={e => setWorkshopForm(f => ({ ...f, bank_account_number: e.target.value }))} placeholder="12345678" style={{ maxWidth: 160 }} /></div>
            <button className="btn btn-primary" onClick={saveWorkshop} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>

          <div className="card" style={{ maxWidth: 600 }}>
            <div className="card-title">Stripe Standard Card Payments</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>
              {STANDARD_CONNECT_MESSAGE}
            </div>
            {connectMsg && <div className="alert alert-success" style={{ marginBottom: 12 }}>{connectMsg}</div>}
            {connectError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{connectError}</div>}
            <div className="job-status-row" style={{ marginBottom: 14 }}>
              <StatusPill tone={stripeStandardReady ? 'green' : 'orange'}>
                {stripeStandardReady ? 'Standard ready' : 'Standard setup needed'}
              </StatusPill>
              <StatusPill tone={workshopForm.stripe_connect_charges_enabled ? 'green' : 'muted'}>Charges</StatusPill>
              <StatusPill tone={workshopForm.stripe_connect_payouts_enabled ? 'green' : 'muted'}>Payouts</StatusPill>
            </div>
            {workshopForm.stripe_connected_account_id && (
              <div className="text-muted fs12" style={{ marginBottom: 12 }}>
                Stripe account: <span className="mono">{workshopForm.stripe_connected_account_id}</span>
                {workshopForm.stripe_connect_account_type ? ` - ${workshopForm.stripe_connect_account_type}` : ''}
                {workshopForm.stripe_connect_last_checked_at ? ` - checked ${fmtDateTime(workshopForm.stripe_connect_last_checked_at)}` : ''}
              </div>
            )}
            {workshopForm.stripe_connect_disabled_reason && (
              <div className="alert alert-error" style={{ marginBottom: 12 }}>
                Stripe needs attention: {workshopForm.stripe_connect_disabled_reason}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={startStripeConnect} disabled={localTestMode || connectBusy === 'start'} title={localTestMode ? 'Disabled in isolated local testing' : undefined}>
                {connectBusy === 'start' ? 'Opening Stripe...' : (workshopForm.stripe_connected_account_id ? 'Reconnect Stripe Standard' : 'Connect Stripe Standard')}
              </button>
              <button className="btn btn-ghost" onClick={() => refreshStripeConnectStatus()} disabled={localTestMode || connectBusy === 'status'} title={localTestMode ? 'Disabled in isolated local testing' : undefined}>
                {connectBusy === 'status' ? 'Checking...' : 'Refresh Status'}
              </button>
              {stripeStandardConnected && (
                <button className="btn btn-ghost" onClick={disconnectStripeConnect} disabled={localTestMode || connectBusy === 'disconnect'} title={localTestMode ? 'Disabled in isolated local testing' : undefined}>
                  {connectBusy === 'disconnect' ? 'Disconnecting...' : 'Disconnect Stripe'}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── KITS TAB ── */}
      {activeTab === 'stock' && isOwner && (
        <StockCatalogueTab workshopId={workshopId} supabase={supabase} />
      )}

      {/* ── KITS TAB ── */}
      {activeTab === 'kits' && isOwner && (
        <KitLibraryTab workshopId={workshopId} supabase={supabase} />
      )}

      {/* ── SPECS TAB ── */}
      {activeTab === 'specs' && isOwner && (
        <ComponentSpecsAdmin workshopId={workshopId} />
      )}

      {/* Import tab */}
      {activeTab === 'import' && isOwner && (
        <LegacyImport />
      )}

      {/* ── TEAM TAB ── */}
      {activeTab === 'team' && isOwner && (
        <>
          <div className="card" style={{ maxWidth: 560, marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 4 }}>Invite a Technician</div>
            <div className="text-muted fs12" style={{ marginBottom: 16 }}>They'll receive an email invite and get access to this workshop. Covered under your existing subscription.</div>
            {inviteMsg && <div className="alert alert-success" style={{ marginBottom: 14 }}>{inviteMsg}</div>}
            {inviteError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{inviteError}</div>}
            <form onSubmit={sendInvite} style={{ display: 'flex', gap: 10 }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="tech@example.com"
                style={{ flex: 1 }}
                required
              />
              <button className="btn btn-primary" type="submit" disabled={localTestMode || inviting} title={localTestMode ? 'Disabled in isolated local testing' : undefined}>
                {inviting ? <><span className="spinner" />&nbsp;Sending…</> : 'Send Invite'}
              </button>
            </form>
          </div>

          <div className="card" style={{ maxWidth: 760, marginBottom: 20 }}>
            <div className="section-head compact" style={{ marginBottom: 14 }}>
              <div>
                <div className="card-title" style={{ marginBottom: 4 }}>Calendar Capacity</div>
                <div className="text-muted fs12">Workshop capacity is independent of technician capacity.</div>
              </div>
              <span className="section-meta">Weekly planning</span>
            </div>
            {capacityMsg && <div className="alert alert-success" style={{ marginBottom: 12 }}>{capacityMsg}</div>}
            {capacityError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{capacityError}</div>}
            <div className="grid-2">
              <div className="field">
                <label>Workshop Bookable Hours / Week</label>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={workshopForm.weekly_bookable_hours}
                  onChange={e => setWorkshopForm(f => ({ ...f, weekly_bookable_hours: e.target.value }))}
                />
                <div className="field-hint">Default is 30 hours. Public booking max jobs per day is unchanged.</div>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {capacityTeamForm.length ? capacityTeamForm.map(member => (
                <div key={member.id} className="capacity-settings-row">
                  <div className="capacity-settings-member">
                    <strong>{member.full_name || 'Unnamed team member'}</strong>
                    <span>{member.role || 'team'}</span>
                  </div>
                  <div className="field compact-field">
                    <label>Hours / Week</label>
                    <input
                      type="number"
                      min="0"
                      step="0.25"
                      value={member.weekly_bookable_hours}
                      onChange={e => setCapacityMemberField(member.id, 'weekly_bookable_hours', e.target.value)}
                      placeholder="No separate ceiling"
                    />
                  </div>
                  <div className="field compact-field">
                    <label>Calendar Colour</label>
                    <div className="capacity-colour-control">
                      <input
                        type="color"
                        value={/^#[0-9a-f]{6}$/i.test(member.calendar_colour || '') ? member.calendar_colour : '#4ea3ff'}
                        onChange={e => setCapacityMemberField(member.id, 'calendar_colour', e.target.value)}
                        aria-label={`${member.full_name || 'Technician'} calendar colour`}
                      />
                      <input
                        value={member.calendar_colour || ''}
                        onChange={e => setCapacityMemberField(member.id, 'calendar_colour', e.target.value)}
                        placeholder="#4ea3ff"
                      />
                    </div>
                  </div>
                </div>
              )) : (
                <div className="muted-empty">Invite team members before setting technician capacity.</div>
              )}
            </div>
            <div className="field-hint" style={{ marginTop: 10 }}>Blank technician hours means no separate technician ceiling. 0 means unavailable unless an owner overrides over-capacity.</div>
            <div className="action-row" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={saveCapacitySettings} disabled={capacitySaving}>
                {capacitySaving ? 'Saving...' : 'Save Calendar Capacity'}
              </button>
            </div>
          </div>

          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Team Members</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden', maxWidth: 560 }}>
            <table>
              <thead>
                <tr><th>Name</th><th>Role</th><th></th></tr>
              </thead>
              <tbody>
                {team.map(m => (
                  <tr key={m.id}>
                    <td className="text-white">{m.full_name || <span className="text-muted">—</span>}</td>
                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
                        color: m.role === 'owner' ? 'var(--text)' : 'var(--muted)',
                        background: 'var(--black)', borderRadius: 4, padding: '2px 7px',
                        border: `1px solid ${m.role === 'owner' ? 'var(--field-border)' : 'var(--border)'}`
                      }}>{m.role}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {m.role !== 'owner' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                          onClick={() => { if (window.confirm('Remove this technician from the workshop?')) removeMember(m.id) }}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── SUBSCRIPTION TAB ── */}
      {activeTab === 'subscription' && isOwner && (
        <div className="card" style={{ maxWidth: 500 }}>
          <div className="card-title">Subscription</div>
          <div className="detail-row"><span className="detail-label">Status</span><span className="detail-val" style={{ color: 'var(--green)', fontWeight: 700, textTransform: 'capitalize' }}>{workshop?.subscription_status || 'inactive'}</span></div>
          <div className="detail-row"><span className="detail-label">Plan</span><span className="detail-val">Rebound Monthly — £99.99/month</span></div>
          <div className="detail-row"><span className="detail-label">Next renewal</span><span className="detail-val">{workshop?.subscription_end ? new Date(workshop.subscription_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</span></div>
          <div className="detail-row"><span className="detail-label">Team members</span><span className="detail-val">{team.length} user{team.length !== 1 ? 's' : ''} — all included</span></div>
          <div style={{ marginTop: 16, fontSize: 13, color: 'var(--muted)' }}>To cancel or update your payment method, contact support.</div>
          <div className="legal-footer" style={{ marginTop: 16, textAlign: 'left' }}>
            <a onClick={() => window.open('/legal', '_blank')}>Terms of Service</a>
            {' · '}
            <a onClick={() => window.open('/legal', '_blank')}>Privacy Policy</a>
          </div>
        </div>
      )}

      {/* ── EDIT SERVICE MODAL ── */}
      {editingService && editingService.module_key === 'general' && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <div className="modal-header">
              <div className="modal-title">{editingService.id ? 'Edit General Service' : 'Add General Service'}</div>
              <button className="close-btn" onClick={() => setEditingService(null)}>×</button>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Category</label>
                <select
                  value={editingService.service_category_id || ''}
                  onChange={e => {
                    const category = serviceCategories.find(item => item.id === e.target.value)
                    setEditingService(s => ({
                      ...s,
                      service_category_id: category?.id || null,
                      category: category?.key || 'general',
                    }))
                  }}
                >
                  <option value="">Uncategorised</option>
                  {generalCategoryOptions.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}{category.active === false ? ' (archived)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Pricing Mode</label>
                <select
                  value={editingService.pricing_mode || 'fixed'}
                  onChange={e => setEditingService(s => ({ ...s, pricing_mode: e.target.value }))}
                >
                  {GENERAL_PRICING_MODES.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Service Name</label>
              <input
                value={editingService.name}
                onChange={e => setEditingService(s => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Workshop Assessment"
              />
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Base Price (£)</label>
                <input
                  type="number"
                  value={editingService.base_price}
                  onChange={e => setEditingService(s => ({ ...s, base_price: e.target.value }))}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="field">
                <label>Expected Labour Minutes</label>
                <input
                  type="number"
                  value={editingService.expected_labour_minutes || 0}
                  onChange={e => setEditingService(s => ({ ...s, expected_labour_minutes: e.target.value }))}
                  min="0"
                  step="5"
                />
              </div>
            </div>
            <div className="field">
              <label>Service Card</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={editingService.service_card_selection || ''}
                  onChange={e => setEditingService(service => ({ ...service, service_card_selection: e.target.value }))}
                  style={{ flex: 1 }}
                >
                  <option value="">Select an approved Service Card</option>
                  {groupServiceCards(serviceCardBlueprints).map(group => (
                    <optgroup key={group.family} label={group.family}>
                      {group.options.map(card => (
                        <option key={serviceCardSelectionValue(card)} value={serviceCardSelectionValue(card)}>
                          {card.name} · v{card.version}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={!editingService.service_card_selection}
                  onClick={() => {
                    const selected = serviceCardBlueprints.find(card => serviceCardSelectionValue(card) === editingService.service_card_selection)
                    setPreviewingServiceCard(selected || null)
                  }}
                >
                  Preview
                </button>
              </div>
              {(!editingService.service_card_selection || Number(editingService.expected_labour_minutes || 0) <= 0) && (
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  Setup required: an approved Service Card and positive Expected Labour Minutes are required before activation.
                </div>
              )}
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Visibility</label>
                <select
                  value={editingService.visibility || 'internal'}
                  onChange={e => setEditingService(s => ({ ...s, visibility: e.target.value, active: e.target.value !== 'archived' }))}
                >
                  {GENERAL_VISIBILITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Booking Type</label>
                <select
                  value={editingService.booking_type || 'assessment_first'}
                  onChange={e => setEditingService(s => ({ ...s, booking_type: e.target.value, is_addon: e.target.value === 'internal_addon' }))}
                >
                  {GENERAL_BOOKING_TYPES.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Customer-Facing Description</label>
              <textarea
                value={editingService.description || ''}
                onChange={e => setEditingService(s => ({ ...s, description: e.target.value }))}
                style={{ minHeight: 90 }}
              />
            </div>
            <div className="field">
              <label>Internal Notes</label>
              <textarea
                value={editingService.internal_notes || ''}
                onChange={e => setEditingService(s => ({ ...s, internal_notes: e.target.value }))}
                style={{ minHeight: 90 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={saveService} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => setEditingService(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {previewingServiceCard && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <div className="modal-header">
              <div>
                <div className="modal-title">{previewingServiceCard.name}</div>
                <div className="text-muted fs12">{previewingServiceCard.family} · version {previewingServiceCard.version}</div>
              </div>
              <button className="close-btn" onClick={() => setPreviewingServiceCard(null)}>×</button>
            </div>
            {serviceCardPreviewItems(previewingServiceCard).map(stage => (
              <div key={stage.stage} style={{ marginBottom: 18 }}>
                <div className="sec" style={{ marginTop: 0 }}>{stage.label}</div>
                {stage.items.map(item => (
                  <div key={item.item_key} className="job-modal-callout" style={{ marginBottom: 8 }}>
                    <div className="fw7">{item.label}</div>
                    {item.technician_guidance && <div className="text-muted fs12" style={{ marginTop: 3 }}>{item.technician_guidance}</div>}
                  </div>
                ))}
              </div>
            ))}
            <button className="btn btn-primary" onClick={() => setPreviewingServiceCard(null)}>Done</button>
          </div>
        </div>
      )}

      {editingService && editingService.module_key !== 'general' && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editingService.id ? 'Edit Service' : 'Add Service'}</div>
              <button className="close-btn" onClick={() => setEditingService(null)}>×</button>
            </div>
            <div className="grid-2">
              <div className="field"><label>Category</label>
                <select value={editingService.category} onChange={e => setEditingService(s => ({ ...s, category: e.target.value }))}>
                  <option value="fork">Fork</option>
                  <option value="shock">Shock</option>
                  <option value="dropper">Dropper</option>
                  <option value="brake">Brake</option>
                  <option value="combined">Combined</option>
                  <option value="other">Other / Misc</option>
                </select>
              </div>
              <div className="field"><label>Brand</label>
                <select value={editingService.brand} onChange={e => setEditingService(s => ({ ...s, brand: e.target.value }))}>
                  <option value="any">Any / All</option>
                  <option value="fox">Fox</option>
                  <option value="rockshox">RockShox</option>
                  <option value="sram">SRAM</option>
                  <option value="shimano">Shimano</option>
                  <option value="magura">Magura</option>
                  <option value="hope">Hope</option>
                  <option value="trp">TRP</option>
                  <option value="hayes">Hayes</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="field"><label>Service Name</label><input value={editingService.name} onChange={e => setEditingService(s => ({ ...s, name: e.target.value }))} placeholder="e.g. Fox 38mm Full Service" /></div>
            <div className="grid-2">
              <div className="field"><label>Price (£)</label><input type="number" value={editingService.base_price} onChange={e => setEditingService(s => ({ ...s, base_price: e.target.value }))} placeholder="0.00" step="0.01" /></div>
              <div className="field"><label>Expected Labour Minutes</label><input type="number" value={editingService.expected_labour_minutes || 0} onChange={e => setEditingService(s => ({ ...s, expected_labour_minutes: e.target.value }))} min="0" step="5" /></div>
            </div>
            <div className="field"><label>Description / What's Included</label><textarea value={editingService.description || ''} onChange={e => setEditingService(s => ({ ...s, description: e.target.value }))} /></div>
            <label className="check-item" style={{ marginBottom: 16 }}>
              <input type="checkbox" checked={!!editingService.is_addon} onChange={e => setEditingService(s => ({ ...s, is_addon: e.target.checked }))} />
              This is an optional add-on / upgrade (not a base service)
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={saveService} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => setEditingService(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editingCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editingCategory.id ? 'Edit General Category' : 'Add General Category'}</div>
              <button className="close-btn" onClick={() => setEditingCategory(null)}>×</button>
            </div>
            <div className="grid-2">
              <div className="field">
                <label>Category Name</label>
                <input
                  value={editingCategory.name}
                  onChange={e => setEditingCategory(category => ({ ...category, name: e.target.value }))}
                  placeholder="e.g. Wheels"
                />
              </div>
              <div className="field">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={editingCategory.sort_order || 0}
                  onChange={e => setEditingCategory(category => ({ ...category, sort_order: e.target.value }))}
                  min="0"
                  step="1"
                />
              </div>
            </div>
            <label className="check-item" style={{ marginBottom: 16 }}>
              <input
                type="checkbox"
                checked={editingCategory.active !== false}
                onChange={e => setEditingCategory(category => ({ ...category, active: e.target.checked }))}
              />
              Category is active
            </label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={saveGeneralCategory} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => setEditingCategory(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Stock Catalogue Tab — workshop-wide parts and materials
// ─────────────────────────────────────────────────────────────
function stockFormFromItem(item = {}) {
  return {
    id: item.id || '',
    name: item.name || '',
    category: item.category || '',
    brand: item.brand || '',
    manufacturer: item.manufacturer || '',
    supplier: item.supplier || '',
    internal_code: item.internal_code || '',
    supplier_part_number: item.supplier_part_number || '',
    barcode_gtin: item.barcode_gtin || '',
    description: item.description || '',
    notes: item.notes || '',
    unit_type: item.unit_type || 'each',
    quantity_on_hand: item.quantity_on_hand ?? '',
    reorder_level: item.reorder_level ?? '',
    cost_price: item.cost_price ?? '',
    sell_price: item.sell_price ?? '',
    tax_treatment: item.tax_treatment || 'not_set',
    active: item.active !== false,
  }
}

function StockCatalogueTab({ workshopId, supabase }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [missingSchema, setMissingSchema] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [lowOnly, setLowOnly] = useState(false)
  const [adjustments, setAdjustments] = useState({})
  const [saving, setSaving] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const filteredItems = items
    .filter(item => showArchived ? true : item.active !== false && !item.archived_at)
    .filter(item => !lowOnly || isLowStock(item))
    .filter(item => !search.trim() || stockItemSearchText(item).includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      if (isLowStock(a) !== isLowStock(b)) return isLowStock(a) ? -1 : 1
      const categorySort = String(a.category || '').localeCompare(String(b.category || ''))
      if (categorySort !== 0) return categorySort
      return String(a.name || '').localeCompare(String(b.name || ''))
    })

  const lowCount = items.filter(isLowStock).length
  const activeCount = items.filter(item => item.active !== false && !item.archived_at).length

  const loadStock = useCallback(async () => {
    if (!workshopId) return
    setLoading(true)
    setError('')
    try {
      const { data, error: loadError } = await supabase
        .from('stock_items')
        .select('*')
        .eq('workshop_id', workshopId)
        .order('category')
        .order('name')

      if (loadError) throw loadError
      setItems(data || [])
      setMissingSchema(false)
    } catch (err) {
      if (isMissingStockSchemaError(err)) {
        setItems([])
        setMissingSchema(true)
      } else {
        setError(err.message || 'Could not load stock.')
      }
    } finally {
      setLoading(false)
    }
  }, [workshopId, supabase])

  useEffect(() => {
    loadStock()
  }, [loadStock])

  function setItemField(key, value) {
    setEditingItem(current => ({ ...current, [key]: value }))
  }

  function newStockItem() {
    setEditingItem(stockFormFromItem({ quantity_on_hand: 0, reorder_level: 0, cost_price: 0, sell_price: 0 }))
    setError('')
    setNotice('')
  }

  async function saveStockItem() {
    if (!editingItem) return
    const name = String(editingItem.name || '').trim()
    if (!name) {
      setError('Stock item name is required.')
      return
    }

    const payload = {
      workshop_id: workshopId,
      name,
      category: String(editingItem.category || '').trim(),
      brand: String(editingItem.brand || '').trim(),
      manufacturer: String(editingItem.manufacturer || '').trim(),
      supplier: String(editingItem.supplier || '').trim(),
      internal_code: String(editingItem.internal_code || '').trim(),
      supplier_part_number: String(editingItem.supplier_part_number || '').trim(),
      barcode_gtin: String(editingItem.barcode_gtin || '').trim(),
      description: String(editingItem.description || '').trim(),
      notes: String(editingItem.notes || '').trim(),
      unit_type: editingItem.unit_type || 'each',
      reorder_level: stockQuantity(editingItem.reorder_level),
      cost_price: Number(editingItem.cost_price || 0),
      sell_price: Number(editingItem.sell_price || 0),
      tax_treatment: editingItem.tax_treatment || 'not_set',
      active: editingItem.active !== false,
    }

    if (!editingItem.id) {
      payload.quantity_on_hand = stockQuantity(editingItem.quantity_on_hand)
    }

    setSaving('stock-item')
    setError('')
    setNotice('')
    try {
      const result = editingItem.id
        ? await supabase.from('stock_items').update(payload).eq('id', editingItem.id).eq('workshop_id', workshopId)
        : await supabase.from('stock_items').insert(payload)
      if (result.error) throw result.error
      setEditingItem(null)
      await loadStock()
      setNotice(editingItem.id ? 'Stock item updated.' : 'Stock item created.')
    } catch (err) {
      setError(err.message || 'Could not save stock item.')
    } finally {
      setSaving('')
    }
  }

  async function setItemArchived(item, archived) {
    setSaving(`archive-${item.id}`)
    setError('')
    setNotice('')
    try {
      const { error: updateError } = await supabase
        .from('stock_items')
        .update({ active: !archived })
        .eq('id', item.id)
        .eq('workshop_id', workshopId)
      if (updateError) throw updateError
      await loadStock()
      setNotice(archived ? 'Stock item archived.' : 'Stock item restored.')
    } catch (err) {
      setError(err.message || 'Could not update stock item.')
    } finally {
      setSaving('')
    }
  }

  function setAdjustment(id, patch) {
    setAdjustments(current => ({ ...current, [id]: { ...(current[id] || {}), ...patch } }))
  }

  async function adjustStock(item) {
    const adjustment = adjustments[item.id] || {}
    const delta = Number(adjustment.delta || 0)
    if (!Number.isFinite(delta) || delta === 0) {
      setError('Enter a positive or negative quantity adjustment.')
      return
    }
    setSaving(`adjust-${item.id}`)
    setError('')
    setNotice('')
    try {
      const { error: adjustError } = await supabase.rpc('adjust_stock_quantity', {
        p_stock_item_id: item.id,
        p_quantity_delta: delta,
        p_reason: String(adjustment.reason || '').trim() || 'Manual stock adjustment',
      })
      if (adjustError) throw adjustError
      setAdjustments(current => {
        const next = { ...current }
        delete next[item.id]
        return next
      })
      await loadStock()
      setNotice('Stock quantity adjusted.')
    } catch (err) {
      setError(err.message || 'Could not adjust stock quantity.')
    } finally {
      setSaving('')
    }
  }

  if (loading) return <div className="text-muted fs12" style={{ padding: 20 }}>Loading stock…</div>

  if (missingSchema) {
    return (
      <div className="card" style={{ maxWidth: 700 }}>
        <div className="card-title">Stock</div>
        <div className="text-muted fs12">Stock schema v85 has not been applied yet.</div>
      </div>
    )
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="section-head compact" style={{ marginBottom: 12 }}>
          <div>
            <div className="card-title" style={{ marginBottom: 2 }}>Stock</div>
            <div className="text-muted fs12">Workshop-wide parts and materials. Stock is optional and works with Suspension and General jobs.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={newStockItem}>+ Add Stock Item</button>
        </div>

        <div className="grid-3">
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Search</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, SKU, supplier, category..." />
          </div>
          <label className="check-row" style={{ alignSelf: 'end', marginBottom: 8 }}>
            <input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} /> Low stock only ({lowCount})
          </label>
          <label className="check-row" style={{ alignSelf: 'end', marginBottom: 8 }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Show archived
          </label>
        </div>

        <div className="fs12 text-muted" style={{ marginTop: 10 }}>
          {activeCount} active item{activeCount === 1 ? '' : 's'} · {lowCount} low-stock alert{lowCount === 1 ? '' : 's'}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {notice && <div className="alert alert-success" style={{ marginBottom: 16 }}>{notice}</div>}

      {filteredItems.length === 0 ? (
        <div className="empty"><p>No stock items match the current filters.</p></div>
      ) : (
        <div className="stock-table-wrap card" style={{ padding: 0, overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Cost / Sell</th>
                <th>Supplier</th>
                <th>Adjust</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const adjustment = adjustments[item.id] || {}
                const archived = item.active === false || Boolean(item.archived_at)
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="text-white fw7">{stockItemTitle(item)}</div>
                      <div className="text-muted fs12">{stockItemMeta(item) || 'No category/code set'}</div>
                      {archived && <StatusPill tone="muted" style={{ marginTop: 4 }}>Archived</StatusPill>}
                    </td>
                    <td>
                      <div className="text-white fw7">{stockQuantityLabel(item.quantity_on_hand, item.unit_type)}</div>
                      <div className="text-muted fs12">Reorder at {stockQuantityLabel(item.reorder_level, item.unit_type)}</div>
                      {isLowStock(item) && <StatusPill tone="orange" style={{ marginTop: 4 }}>Low stock</StatusPill>}
                    </td>
                    <td>
                      <div className="text-muted fs12">Cost {stockMoneyLabel(item.cost_price)}</div>
                      <div className="text-green fw7">Sell {stockMoneyLabel(item.sell_price)}</div>
                    </td>
                    <td className="text-muted fs12">
                      {item.supplier || '—'}
                      {item.supplier_part_number && <div className="mono">{item.supplier_part_number}</div>}
                    </td>
                    <td style={{ minWidth: 230 }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <input
                          type="number"
                          step="0.001"
                          value={adjustment.delta || ''}
                          onChange={e => setAdjustment(item.id, { delta: e.target.value })}
                          placeholder="+/- quantity"
                          disabled={archived}
                        />
                        <input
                          value={adjustment.reason || ''}
                          onChange={e => setAdjustment(item.id, { reason: e.target.value })}
                          placeholder="Reason"
                          disabled={archived}
                        />
                        <button className="btn btn-ghost btn-sm" onClick={() => adjustStock(item)} disabled={archived || saving === `adjust-${item.id}`}>
                          {saving === `adjust-${item.id}` ? 'Adjusting...' : 'Adjust Qty'}
                        </button>
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingItem(stockFormFromItem(item))}>Edit</button>
                      {' '}
                      <button className="btn btn-ghost btn-sm" onClick={() => setItemArchived(item, !archived)} disabled={saving === `archive-${item.id}`}>
                        {archived ? 'Restore' : 'Archive'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingItem && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <div className="modal-header">
              <div>
                <div className="modal-title">{editingItem.id ? 'Edit Stock Item' : 'Add Stock Item'}</div>
                <div className="text-muted fs12" style={{ marginTop: 3 }}>Catalogue details are workshop-wide and independent of service kits.</div>
              </div>
              <button className="close-btn" onClick={() => setEditingItem(null)}>×</button>
            </div>

            <div className="grid-3">
              <div className="field">
                <label>Name</label>
                <input value={editingItem.name} onChange={e => setItemField('name', e.target.value)} placeholder="Brake pads, cable, oil..." />
              </div>
              <div className="field">
                <label>Category</label>
                <input value={editingItem.category} onChange={e => setItemField('category', e.target.value)} placeholder="Brakes, Drivetrain, Workshop..." />
              </div>
              <div className="field">
                <label>Unit</label>
                <select value={editingItem.unit_type} onChange={e => setItemField('unit_type', e.target.value)}>
                  {STOCK_UNIT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid-3">
              <div className="field">
                <label>Brand</label>
                <input value={editingItem.brand} onChange={e => setItemField('brand', e.target.value)} />
              </div>
              <div className="field">
                <label>Manufacturer</label>
                <input value={editingItem.manufacturer} onChange={e => setItemField('manufacturer', e.target.value)} />
              </div>
              <div className="field">
                <label>Supplier</label>
                <input value={editingItem.supplier} onChange={e => setItemField('supplier', e.target.value)} />
              </div>
            </div>

            <div className="grid-3">
              <div className="field">
                <label>SKU / Internal Code</label>
                <input value={editingItem.internal_code} onChange={e => setItemField('internal_code', e.target.value)} />
              </div>
              <div className="field">
                <label>Supplier Part Number</label>
                <input value={editingItem.supplier_part_number} onChange={e => setItemField('supplier_part_number', e.target.value)} />
              </div>
              <div className="field">
                <label>Barcode / GTIN</label>
                <input value={editingItem.barcode_gtin} onChange={e => setItemField('barcode_gtin', e.target.value)} />
              </div>
            </div>

            <div className="grid-4">
              <div className="field">
                <label>{editingItem.id ? 'Quantity on Hand' : 'Opening Quantity'}</label>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={editingItem.quantity_on_hand}
                  onChange={e => setItemField('quantity_on_hand', e.target.value)}
                  disabled={Boolean(editingItem.id)}
                />
                {editingItem.id && <div className="field-hint">Use Adjust Qty in the table to change quantity.</div>}
              </div>
              <div className="field">
                <label>Reorder Level</label>
                <input type="number" min="0" step="0.001" value={editingItem.reorder_level} onChange={e => setItemField('reorder_level', e.target.value)} />
              </div>
              <div className="field">
                <label>Cost Price (£)</label>
                <input type="number" min="0" step="0.01" value={editingItem.cost_price} onChange={e => setItemField('cost_price', e.target.value)} />
              </div>
              <div className="field">
                <label>Sell Price (£)</label>
                <input type="number" min="0" step="0.01" value={editingItem.sell_price} onChange={e => setItemField('sell_price', e.target.value)} />
              </div>
            </div>

            <div className="field">
              <label>Description</label>
              <textarea value={editingItem.description} onChange={e => setItemField('description', e.target.value)} style={{ minHeight: 76 }} />
            </div>
            <div className="field">
              <label>Internal Notes</label>
              <textarea value={editingItem.notes} onChange={e => setItemField('notes', e.target.value)} style={{ minHeight: 76 }} />
            </div>

            <label className="check-item" style={{ marginBottom: 16 }}>
              <input type="checkbox" checked={editingItem.active !== false} onChange={e => setItemField('active', e.target.checked)} />
              Active stock item
            </label>

            <div className="action-row">
              <button className="btn btn-primary" onClick={saveStockItem} disabled={saving === 'stock-item'}>{saving === 'stock-item' ? 'Saving...' : 'Save Stock Item'}</button>
              <button className="btn btn-ghost" onClick={() => setEditingItem(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────
// Kit Library Tab — view and manage workshop kit prices
// ─────────────────────────────────────────────────────────────
function KitLibraryTab({ workshopId, supabase }) {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingPrice, setEditingPrice] = useState({})
  const [saving, setSaving] = useState({})
  const [showBulk, setShowBulk] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [bulkPreview, setBulkPreview] = useState([])
  const [bulkError, setBulkError] = useState('')
  const [bulkImporting, setBulkImporting] = useState(false)
  const [bulkResult, setBulkResult] = useState(null)
  const [bulkFileName, setBulkFileName] = useState('')

  function normaliseCsvHeader(value) {
    const key = String(value || '')
      .replace(/^\uFEFF/, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')

    const aliases = {
      part: 'part_number',
      part_no: 'part_number',
      partnumber: 'part_number',
      kit_part_number: 'part_number',
      kit_number: 'part_number',
      service_kit: 'description',
      kit: 'description',
      kit_description: 'description',
      name: 'description',
      model: 'model_name',
      product: 'model_name',
      cost: 'unit_cost',
      price: 'unit_cost',
      unit_price: 'unit_cost',
      modelcode: 'model_code',
    }

    return aliases[key] || key
  }

  function parseCsvLine(line) {
    const values = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      const next = line[i + 1]
      if (char === '"' && inQuotes && next === '"') {
        current += '"'
        i += 1
      } else if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    values.push(current.trim())
    return values
  }

  function normaliseMoney(value) {
    const cleaned = String(value || '').replace(/[£,]/g, '').trim()
    if (!cleaned) return null
    const amount = Number(cleaned)
    return Number.isFinite(amount) && amount >= 0 ? amount : null
  }

  function cleanSupplierText(value) {
    return String(value || '')
      .replace(/&quot;/g, '"')
      .replace(/&#34;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  function parseBulkCSV(text) {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim())
    if (lines.length < 2) { setBulkError('Need at least a header row and one data row.'); setBulkPreview([]); return }
    const header = parseCsvLine(lines[0]).map(normaliseCsvHeader)
    const required = ['part_number', 'description']
    const missing = required.filter(r => !header.includes(r))
    if (missing.length) { setBulkError(`Missing columns: ${missing.join(', ')}`); setBulkPreview([]); return }

    const rows = []
    let skipped = 0
    for (let i = 1; i < lines.length; i++) {
      const vals = parseCsvLine(lines[i])
      const row = {}
      header.forEach((h, idx) => { row[h] = vals[idx] || '' })
      if (!row.part_number || !row.description) { skipped++; continue }
      const modelCode = row.model_code ? row.model_code.toUpperCase().trim() : ''
      rows.push({
        model_code: modelCode,
        part_number: cleanSupplierText(row.part_number).toUpperCase(),
        description: cleanSupplierText(row.description),
        model_name: cleanSupplierText(row.model_name) || (modelCode ? modelCode : 'General kit'),
        unit_cost: normaliseMoney(row.unit_cost),
        supplier: cleanSupplierText(row.supplier),
      })
    }
    if (!rows.length) { setBulkError('No valid rows found.'); setBulkPreview([]); return }
    setBulkError(skipped ? `${skipped} row${skipped === 1 ? '' : 's'} skipped because part_number or description was missing.` : '')
    setBulkPreview(rows)
  }

  async function handleBulkFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      setBulkFileName(file.name)
      setBulkText(text)
      setBulkPreview([])
      setBulkError('')
      setBulkResult(null)
      parseBulkCSV(text)
    } catch {
      setBulkError('Could not read that CSV file.')
      setBulkPreview([])
    } finally {
      e.target.value = ''
    }
  }

  async function importBulk() {
    if (!bulkPreview.length) return
    setBulkImporting(true)
    let imported = 0; let updated = 0; let failed = 0

    for (const row of bulkPreview) {
      try {
        // Upsert into shared library
        const { data: upserted, error } = await supabase
          .from('kit_library')
          .upsert({
            model_code: row.model_code,
            model_name: row.model_name,
            part_number: row.part_number,
            description: row.description,
            contributed_by: workshopId,
          }, { onConflict: 'model_code,part_number' })
          .select('id')
          .single()

        if (error || !upserted?.id) { failed++; continue }

        // Upsert workshop price if provided
        if (row.unit_cost !== null) {
          await supabase.from('kit_prices').upsert({
            kit_id: upserted.id,
            workshop_id: workshopId,
            unit_cost: row.unit_cost,
            supplier: row.supplier,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'kit_id,workshop_id' })
          updated++
        } else {
          imported++
        }
      } catch { failed++ }
    }

    // Reload
    const { data } = await supabase
      .from('kit_library')
      .select('id, model_code, model_name, part_number, description, created_at, kit_prices(unit_cost, supplier, updated_at)')
      .order('model_code').order('created_at')
    setKits(data || [])
    setBulkImporting(false)
    setBulkResult({ imported: imported + updated, failed })
    setBulkPreview([])
    setBulkText('')
  }

  function downloadTemplate() {
    const csv = 'part_number,description,unit_cost,supplier,model_code,model_name\n00.4318.037.017,Vivid Coil Ultimate Service Kit,19.82,Zyrofisher,RS-VIVC-ULT-C1,RockShox Vivid Coil Ultimate C1\n00.4318.084.004,Pike Select Plus Lower Leg Service Kit,14.50,Silverfish,FS-PIKE-SELP-B4,RockShox Pike Select Plus B4\n00.4318.025.209,200 Hour / 1 Year Service Kit,46.37,Zyrofisher,,\n'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'rebound_kit_template.csv'
    document.body.appendChild(a); a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 2000)
  }

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('kit_library')
        .select(`
          id, model_code, model_name, part_number, description, created_at,
          kit_prices(unit_cost, supplier, updated_at)
        `)
        .order('model_code')
        .order('created_at')
      setKits(data || [])
      setLoading(false)
    }
    load()
  }, [supabase])

  async function savePrice(kit) {
    const ep = editingPrice[kit.id]
    if (!ep?.unit_cost) return
    setSaving(s => ({ ...s, [kit.id]: true }))
    await supabase.from('kit_prices').upsert({
      kit_id: kit.id,
      workshop_id: workshopId,
      unit_cost: Number(ep.unit_cost),
      supplier: ep.supplier || '',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'kit_id,workshop_id' })
    // Reload
    const { data } = await supabase
      .from('kit_library')
      .select('id, model_code, model_name, part_number, description, created_at, kit_prices(unit_cost, supplier, updated_at)')
      .order('model_code').order('created_at')
    setKits(data || [])
    setSaving(s => ({ ...s, [kit.id]: false }))
    setEditingPrice(e => { const n = { ...e }; delete n[kit.id]; return n })
  }

  if (loading) return <div className="text-muted fs12" style={{ padding: 20 }}>Loading kit library…</div>

  // Group by model code
  const grouped = {}
  kits.forEach(k => {
    if (!grouped[k.model_code]) grouped[k.model_code] = { model_name: k.model_name, kits: [] }
    grouped[k.model_code].kits.push(k)
  })

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 520 }}>
          Part numbers are shared across all Rebound workshops. Your prices are private to your workshop.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}>⬇ CSV Template</button>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowBulk(s => !s); setBulkResult(null) }}>
            {showBulk ? 'Cancel' : '⬆ Bulk Import'}
          </button>
        </div>
      </div>

      {showBulk && (
        <div className="card" style={{ maxWidth: 700, marginBottom: 20 }}>
          <div className="card-title">Bulk Import Service Kits</div>
          <div className="text-muted fs12" style={{ marginBottom: 12 }}>
            Upload a CSV file or paste the rows below. Required columns: <code>part_number, description</code>. Optional: <code>unit_cost, supplier, model_code, model_name</code>.
            A blank <code>model_code</code> saves the row as a general kit.
          </div>
          <input type="file" accept=".csv,text/csv" onChange={handleBulkFile} style={{ marginBottom: 10 }} />
          {bulkFileName && <div className="text-muted fs12" style={{ marginBottom: 10 }}>Loaded file: {bulkFileName}</div>}
          <textarea
            value={bulkText}
            onChange={e => { setBulkText(e.target.value); setBulkPreview([]); setBulkError(''); setBulkResult(null); setBulkFileName('') }}
            placeholder={'part_number,description,unit_cost,supplier,model_code,model_name\n00.4318.037.017,Vivid Coil Ultimate Service Kit,19.82,Zyrofisher,RS-VIVC-ULT-C1,RockShox Vivid Coil Ultimate C1'}
            style={{ minHeight: 160, fontFamily: 'monospace', fontSize: 12, marginBottom: 10 }}
          />
          {bulkError && <div className="alert alert-error" style={{ marginBottom: 10 }}>{bulkError}</div>}
          {bulkResult && (
            <div className="alert alert-success" style={{ marginBottom: 10 }}>
              ✓ Imported {bulkResult.imported} kits{bulkResult.failed > 0 ? ` · ${bulkResult.failed} failed` : ''}.
            </div>
          )}
          {bulkPreview.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 8 }}>
                Preview — {bulkPreview.length} rows
              </div>
              <div style={{ background: 'var(--black)', borderRadius: 6, border: '1px solid var(--border)', maxHeight: 220, overflowY: 'auto' }}>
                {bulkPreview.map((row, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'monospace', color: 'var(--text-2)', minWidth: 140 }}>{row.model_code || 'GENERAL'}</span>
                    <span style={{ fontFamily: 'monospace', color: 'var(--white)', minWidth: 160 }}>{row.part_number}</span>
                    <span style={{ color: 'var(--muted)', flex: 1 }}>{row.description}</span>
                    {row.unit_cost && <span style={{ color: 'var(--green)' }}>£{row.unit_cost.toFixed(2)}</span>}
                    {row.supplier && <span style={{ color: 'var(--muted)' }}>{row.supplier}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {bulkPreview.length === 0
              ? <button className="btn btn-primary btn-sm" onClick={() => parseBulkCSV(bulkText)} disabled={!bulkText.trim()}>Preview import</button>
              : <button className="btn btn-primary btn-sm" onClick={importBulk} disabled={bulkImporting}>{bulkImporting ? 'Importing…' : `Import ${bulkPreview.length} kits`}</button>
            }
            {bulkPreview.length > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setBulkPreview([])}>Re-edit</button>}
          </div>
        </div>
      )}
      {kits.length === 0 && (
        <div className="card" style={{ maxWidth: 700 }}>
          <div className="card-title">Kit Library</div>
          <div className="text-muted fs12">No kits in the library yet. Use Bulk Import to upload your kit list, or add kits from inside a service job.</div>
        </div>
      )}

      {Object.entries(grouped).map(([modelCode, group]) => (
        <div key={modelCode} className="card" style={{ maxWidth: 700, marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 2 }}>{modelCode ? (group.model_name || modelCode) : 'General kits'}</div>
          <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-2)', marginBottom: 14 }}>{modelCode || 'No model code'}</div>
          {group.kits.map(kit => {
            const myPrice = kit.kit_prices?.[0]
            const ep = editingPrice[kit.id]
            return (
              <div key={kit.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: 'var(--white)' }}>{kit.part_number}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{kit.description}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {ep ? (
                      <>
                        <input
                          type="number"
                          value={ep.unit_cost}
                          onChange={e => setEditingPrice(p => ({ ...p, [kit.id]: { ...ep, unit_cost: e.target.value } }))}
                          placeholder="Cost £"
                          step="0.01"
                          style={{ width: 90 }}
                        />
                        <input
                          value={ep.supplier}
                          onChange={e => setEditingPrice(p => ({ ...p, [kit.id]: { ...ep, supplier: e.target.value } }))}
                          placeholder="Supplier"
                          style={{ width: 110 }}
                        />
                        <button className="btn btn-primary btn-sm" onClick={() => savePrice(kit)} disabled={saving[kit.id]}>
                          {saving[kit.id] ? 'Saving…' : 'Save'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingPrice(p => { const n={...p}; delete n[kit.id]; return n })}>Cancel</button>
                      </>
                    ) : (
                      <>
                        {myPrice
                          ? <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>£{Number(myPrice.unit_cost).toFixed(2)}{myPrice.supplier ? ` · ${myPrice.supplier}` : ''}</span>
                          : <span style={{ fontSize: 12, color: 'var(--muted)' }}>No price set</span>
                        }
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditingPrice(p => ({ ...p, [kit.id]: { unit_cost: myPrice?.unit_cost || '', supplier: myPrice?.supplier || '' } }))}
                        >
                          {myPrice ? 'Update price' : 'Set price'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}
